import type { CellFormat } from '../compositor/GridController';
import type { UnifiedUndoCommand } from '../components/parity-dev/authoring/useUnifiedUndoManager';
import { perf } from '../perf/devPerf';
import { WorkerBus } from './WorkerBus';
import {
    buildSpreadsheetEngineSheetId,
    buildSpreadsheetOperationEnvelope,
    buildSpreadsheetStyleFromFormat,
    canonicalizeSpreadsheetValidationRule,
    buildSpreadsheetWorkerCommand,
    getSpreadsheetOperationStyleSidecars,
    getSpreadsheetRoutedResponseRequestId,
    isSpreadsheetCopyPayloadResponsePayload,
    isSpreadsheetDefinedNamesResponsePayload,
    isSpreadsheetConditionalFormatRulesResponsePayload,
    isSpreadsheetDerivedArtifactsResponsePayload,
    isSpreadsheetSparklineGroupsResponsePayload,
    isSpreadsheetDeltaResponsePayload,
    isSpreadsheetEvaluateDraftFormulaResponsePayload,
    isSpreadsheetNamedRangesResponsePayload,
    isSpreadsheetRangeResponsePayload,
    isSpreadsheetSheetListResponsePayload,
    isSpreadsheetValidationIssue,
    isSpreadsheetValidationRulesResponsePayload,
    isSpreadsheetValidationIssuesResponsePayload,
    isSpreadsheetViewportResponsePayload,
    isSpreadsheetValidationDropdownOptionsResponsePayload,
    normalizeSpreadsheetRange,
    normalizeSpreadsheetStyleSidecars,
    type SpreadsheetApplyOpsPayload,
    type SpreadsheetCellAddress,
    type SpreadsheetClipboardPayload,
    type SpreadsheetCopyRangeFormatsToPayload,
    type SpreadsheetCopyRangePayload,
    type SpreadsheetCopyRangeToPayload,
    type SpreadsheetConditionalFormatRule,
    type SpreadsheetConditionalFormatRulesResponsePayload,
    type SpreadsheetDeltaResponsePayload,
    type SpreadsheetDefinedNameScope,
    type SpreadsheetDefinedNamesResponsePayload,
    type SpreadsheetDerivedArtifact,
    type SpreadsheetDerivedArtifactKind,
    type SpreadsheetDerivedArtifactsResponsePayload,
    type SpreadsheetEngineOperationEnvelope,
    type SpreadsheetEngineWorkerCommand,
    type SpreadsheetEvaluateDraftFormulaPayload,
    type SpreadsheetEvaluateDraftFormulaResponsePayload,
    type SpreadsheetFillRangeMode,
    type SpreadsheetFillRangePayload,
    type SpreadsheetFindReplacePayload,
    type SpreadsheetGetRangePayload,
    type SpreadsheetGetViewportPayload,
    type SpreadsheetMoveRangePayload,
    type SpreadsheetNamedRangesResponsePayload,
    type SpreadsheetPasteSpecialMode,
    type SpreadsheetPasteSpecialPayload,
    type SpreadsheetPasteTsvPayload,
    type SpreadsheetPresenceUpdatePayload,
    type SpreadsheetRangeCell,
    type SpreadsheetRangeAddress,
    type SpreadsheetRecalcIncrementalPayload,
    type SpreadsheetRemoveDuplicatesPayload,
    type SpreadsheetSortRangePayload,
    type SpreadsheetDeleteTablePayload,
    type SpreadsheetTableDefinition,
    type SpreadsheetAutoFilterColumnCriteria,
    type SpreadsheetAutoFilterCondition,
    type SpreadsheetDefineNamePayload,
    type SpreadsheetDeleteNamePayload,
    type SpreadsheetSetFrozenPanesPayload,
    type SpreadsheetSetAutoFilterPayload,
    type SpreadsheetUpsertTablePayload,
    type SpreadsheetSetValidationPayload,
    type SpreadsheetUpsertValidationRulePayload,
    type SpreadsheetValidationRule,
    type SpreadsheetValidationDropdownOptionsPayload,
    type SpreadsheetValidationDropdownOptionsResponsePayload,
    type SpreadsheetValidationIssue,
    type SpreadsheetValidationIssuesPayload,
    type SpreadsheetValidationIssuesResponsePayload,
    type SpreadsheetValidationRulesResponsePayload,
    type SpreadsheetSetCommentPayload,
    type SpreadsheetSetHyperlinkPayload,
    type SpreadsheetSetProtectionPayload,
    type SpreadsheetSetSheetProtectionPayload,
    type SpreadsheetSheetListEntry,
    type SpreadsheetSparklineGroup,
    type SpreadsheetSparklineGroupsResponsePayload,
    type SpreadsheetStructuralOperationPayload,
    type SpreadsheetStructuralOperationType,
    type SpreadsheetViewportResponsePayload,
} from '../protocol/spreadsheetEngineProtocol';

export interface CellData {
    value: string;
    formula?: string;
    format?: CellFormat;
    validationId?: string;
    comment?: string;
    hyperlink?: string;
    protection?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
}

export type SpreadsheetFindMatch = {
    col: number;
    row: number;
    data: {
        value: string;
    };
}

export type SpreadsheetMutationError = {
    requestId: string;
    message: string;
    col?: number;
    row?: number;
    targetRange?: SpreadsheetRangeAddress;
    operationLabel?: string;
    validationIssue?: SpreadsheetValidationIssue;
    protectedCell?: SpreadsheetCellAddress;
    value?: string;
    retryWithValidationOverride?: () => void;
};

export type ViewportStreamerOptions = {
    // When true, request spreadsheet viewport data even if a query tab is active.
    forceSpreadsheetViewport?: boolean;
    // When true, allow edit messages through while query tabs are active.
    allowQueryTabEdits?: boolean;
    transformCellInput?: (args: {
        sheetId: string;
        col: number;
        row: number;
        rawInput: string;
    }) => string;
    transformPastedText?: (args: {
        sheetId: string;
        startCol: number;
        startRow: number;
        text: string;
    }) => string;
}

export type SpreadsheetWorkerSheetListEntry = SpreadsheetSheetListEntry;

const STRUCTURED_TABLE_IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_.]*$/;

const structuredReferenceFormulaForColumn = (
    tableName: string,
    columnName: string,
): string | null => {
    const normalizedTableName = String(tableName ?? '').trim();
    const normalizedColumnName = String(columnName ?? '').trim();
    if (!STRUCTURED_TABLE_IDENTIFIER_RE.test(normalizedTableName)) return null;
    if (!normalizedColumnName || normalizedColumnName.startsWith('#') || normalizedColumnName.includes(']')) return null;
    return `=SUM(${normalizedTableName}[[#Data],[${normalizedColumnName}]])`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value && typeof value === 'object' && !Array.isArray(value));

const STYLE_SECTION_KEYS: Array<keyof CellFormat> = [
    'numberFormat',
    'font',
    'fill',
    'border',
    'alignment',
    'protection',
];

const CODELESS_NUMBER_FORMAT_CATEGORIES = new Set([
    'general',
    'number',
    'currency',
    'accounting',
    'percentage',
    'percent',
    'scientific',
    'text',
]);

function shouldReplaceNumberFormatSection(baseValue: unknown, patchValue: unknown): boolean {
    if (!isRecord(patchValue)) return false;
    const category = typeof patchValue.category === 'string'
        ? patchValue.category.trim().toLowerCase()
        : '';
    if (!category) return false;
    if (isRecord(baseValue)) {
        const baseCategory = typeof baseValue.category === 'string'
            ? baseValue.category.trim().toLowerCase()
            : '';
        if (baseCategory && baseCategory !== category) return true;
    }
    return !Object.prototype.hasOwnProperty.call(patchValue, 'code') && CODELESS_NUMBER_FORMAT_CATEGORIES.has(category);
}

function mergeCellFormat(base: CellFormat | undefined | null, patch: CellFormat): CellFormat {
    const merged: CellFormat = { ...(base || {}), ...patch };
    for (const key of STYLE_SECTION_KEYS) {
        const baseValue = base?.[key];
        const patchValue = patch[key];
        if (isRecord(baseValue) && isRecord(patchValue)) {
            if (key === 'numberFormat' && shouldReplaceNumberFormatSection(baseValue, patchValue)) {
                merged[key] = {
                    ...patchValue,
                } as never;
                continue;
            }
            merged[key] = {
                ...baseValue,
                ...patchValue,
            } as never;
        }
    }
    return merged;
}

const isSpreadsheetCellAddressLike = (value: unknown): value is SpreadsheetCellAddress => {
    if (!isRecord(value)) return false;
    return (
        String(value.sheet_id ?? '').trim().length > 0 &&
        Number.isFinite(Number(value.row)) &&
        Number.isFinite(Number(value.col))
    );
};

const parseSpreadsheetStructuredError = (message: string): {
    message?: string;
    validationIssue?: SpreadsheetValidationIssue;
    protectedCell?: SpreadsheetCellAddress;
} | null => {
    const text = String(message ?? '').trim();
    if (!text) return null;
    const candidates = [text, text.replace(/^Error:\s*/, '')];
    for (const rawCandidate of candidates) {
        const candidate = rawCandidate.startsWith('JsValue(') && rawCandidate.endsWith(')')
            ? rawCandidate.slice('JsValue('.length, -1).trim()
            : rawCandidate;
        const jsonStart = candidate.indexOf('{');
        const jsonEnd = candidate.lastIndexOf('}');
        if (jsonStart < 0 || jsonEnd < jsonStart) continue;
        try {
            const parsed = JSON.parse(candidate.slice(jsonStart, jsonEnd + 1));
            if (!isRecord(parsed)) continue;
            const rejected = isRecord(parsed.ValidationRejected) ? parsed.ValidationRejected : null;
            const issue = rejected && isSpreadsheetValidationIssue(rejected.issue)
                ? rejected.issue
                : null;
            if (issue) {
                return {
                    message: String(issue.message ?? '').trim() || undefined,
                    validationIssue: issue,
                };
            }
            const protectedEdit = isRecord(parsed.ProtectedCellEdit) ? parsed.ProtectedCellEdit : null;
            const protectedCell = protectedEdit && isSpreadsheetCellAddressLike(protectedEdit.address)
                ? protectedEdit.address
                : null;
            if (protectedCell) {
                const protectedMessage = protectedEdit
                    ? String(protectedEdit.message ?? '').trim() || undefined
                    : undefined;
                return {
                    message: protectedMessage,
                    protectedCell,
                };
            }
            const invalidPayload = isRecord(parsed.InvalidPayload) ? parsed.InvalidPayload : null;
            const payloadMessage = String(
                invalidPayload?.message
                ?? parsed.message
                ?? '',
            ).trim();
            if (payloadMessage) return { message: payloadMessage };
        } catch {
            // The worker may send legacy plain strings; keep checking candidates.
        }
    }
    return null;
};

const humanizeSpreadsheetMutationError = (message: string): string => {
    const text = String(message ?? '').trim();
    if (!text) return 'The edit was rejected by the spreadsheet engine.';
    const structured = parseSpreadsheetStructuredError(text);
    if (structured?.message) return structured.message;
    return text.replace(/^JsValue\(|\)$/g, '').replace(/^Error:\s*/, '').trim() || 'The edit was rejected by the spreadsheet engine.';
};

const buildSpreadsheetMutationTargetRange = (
    sheetId: string,
    startCol: number,
    startRow: number,
    width: number,
    height: number,
): SpreadsheetRangeAddress => {
    const col = Math.max(0, Math.floor(Number(startCol) || 0));
    const row = Math.max(0, Math.floor(Number(startRow) || 0));
    const w = Math.max(1, Math.floor(Number(width) || 1));
    const h = Math.max(1, Math.floor(Number(height) || 1));
    return {
        sheet_id: sheetId,
        start_row: row,
        start_col: col,
        end_row: row + h - 1,
        end_col: col + w - 1,
    };
};

const getTsvShape = (text: string): { width: number; height: number } => {
    const rows = String(text ?? '').split(/\r\n|\n|\r/);
    if (rows.length > 0 && rows[rows.length - 1] === '') {
        rows.pop();
    }
    if (rows.length <= 0) return { width: 1, height: 1 };
    const width = rows.reduce((max, row) => Math.max(max, row.split('\t').length), 1);
    return { width, height: rows.length };
};

type PendingSpreadsheetRangeRequest = {
    resolve: (data: string[][]) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetRangeCellsRequest = {
    resolve: (data: SpreadsheetRangeCell[][]) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetCopyRequest = {
    resolve: (tsv: string) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetNamedRangesRequest = {
    resolve: (payload: SpreadsheetNamedRangesResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetDefinedNamesRequest = {
    resolve: (payload: SpreadsheetDefinedNamesResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetDraftFormulaRequest = {
    resolve: (payload: SpreadsheetEvaluateDraftFormulaResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetValidationRulesRequest = {
    resolve: (payload: SpreadsheetValidationRulesResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetConditionalFormatRulesRequest = {
    resolve: (payload: SpreadsheetConditionalFormatRulesResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetSparklineGroupsRequest = {
    resolve: (payload: SpreadsheetSparklineGroupsResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetDerivedArtifactsRequest = {
    resolve: (payload: SpreadsheetDerivedArtifactsResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetValidationDropdownOptionsRequest = {
    resolve: (payload: SpreadsheetValidationDropdownOptionsResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingSpreadsheetValidationIssuesRequest = {
    resolve: (payload: SpreadsheetValidationIssuesResponsePayload) => void;
    reject: (error: Error) => void;
};

type PendingOptimisticCellEdit = {
    col: number;
    row: number;
    value: string;
    previous?: CellData;
};

type PendingValidationOverrideMutation = {
    col: number;
    row: number;
    targetRange?: SpreadsheetRangeAddress;
    operationLabel?: string;
    retryWithValidationOverride: () => void;
};

type PendingUndoableMutation = {
    requestId: string;
    label: string;
    operationIds: Set<string>;
};

type PendingUndoRegistrationOptions = {
    label?: string;
    skipUndoRegistration?: boolean;
};

type ProjectionWriteOptions = {
    sheetId?: number | null;
    skipUndoRegistration?: boolean;
    publishAcceptedOperations?: boolean;
};

type PendingUndoCommandOptions = {
    label: string;
    operationIds: string[];
    skipUndoRegistration?: boolean;
};

type PendingMutationCommandOptions = {
    undo?: PendingUndoCommandOptions;
    publishAcceptedOperations?: boolean;
};

export class ViewportStreamer {
    private bus: WorkerBus;
    private options: {
        forceSpreadsheetViewport: boolean;
        allowQueryTabEdits: boolean;
        transformCellInput?: ViewportStreamerOptions['transformCellInput'];
        transformPastedText?: ViewportStreamerOptions['transformPastedText'];
    };
    private unsubscribers: Array<() => void> = [];
    private cellCache: Map<string, CellData> = new Map();
    private callback: (() => void) | null = null;
    private projectionRefreshCallback: (() => void) | null = null;
    private viewportUpdater: (() => void) | null = null;
    private sheetsCallback: ((sheets: SpreadsheetWorkerSheetListEntry[]) => void) | null = null;
    private namedRangesCallback: ((payload: SpreadsheetNamedRangesResponsePayload) => void) | null = null;
    private definedNamesCallback: ((payload: SpreadsheetDefinedNamesResponsePayload) => void) | null = null;
    private validationRulesCallback: ((payload: SpreadsheetValidationRulesResponsePayload) => void) | null = null;
    private conditionalFormatRulesCallback: ((payload: SpreadsheetConditionalFormatRulesResponsePayload) => void) | null = null;
    private sparklineGroupsCallback: ((payload: SpreadsheetSparklineGroupsResponsePayload) => void) | null = null;
    private derivedArtifactsCallback: ((payload: SpreadsheetDerivedArtifactsResponsePayload) => void) | null = null;
    private mutationErrorCallback: ((error: SpreadsheetMutationError) => void) | null = null;
    private findCallback: ((matches: SpreadsheetFindMatch[]) => void) | null = null;
    private pendingRequests: Map<string, PendingSpreadsheetRangeRequest> = new Map();
    private pendingRangeCellsRequests: Map<string, PendingSpreadsheetRangeCellsRequest> = new Map();
    private pendingCopyRequests: Map<string, PendingSpreadsheetCopyRequest> = new Map();
    private pendingNamedRangesRequests: Map<string, PendingSpreadsheetNamedRangesRequest> = new Map();
    private pendingDefinedNamesRequests: Map<string, PendingSpreadsheetDefinedNamesRequest> = new Map();
    private pendingDraftFormulaRequests: Map<string, PendingSpreadsheetDraftFormulaRequest> = new Map();
    private pendingValidationRulesRequests: Map<string, PendingSpreadsheetValidationRulesRequest> = new Map();
    private pendingConditionalFormatRulesRequests: Map<string, PendingSpreadsheetConditionalFormatRulesRequest> = new Map();
    private pendingSparklineGroupsRequests: Map<string, PendingSpreadsheetSparklineGroupsRequest> = new Map();
    private pendingDerivedArtifactsRequests: Map<string, PendingSpreadsheetDerivedArtifactsRequest> = new Map();
    private pendingValidationDropdownOptionsRequests: Map<string, PendingSpreadsheetValidationDropdownOptionsRequest> = new Map();
    private pendingValidationIssuesRequests: Map<string, PendingSpreadsheetValidationIssuesRequest> = new Map();
    private pendingOptimisticCellEdits: Map<string, PendingOptimisticCellEdit> = new Map();
    private pendingValidationOverrideMutations: Map<string, PendingValidationOverrideMutation> = new Map();
    private lastEngineClipboard: SpreadsheetClipboardPayload | null = null;
    private pendingUndoableMutationsByRequestId: Map<string, PendingUndoableMutation> = new Map();
    private pendingUndoableMutationsByOperationId: Map<string, PendingUndoableMutation> = new Map();
    private pendingAcceptedOperationPublicationRequestIds: Set<string> = new Set();
    private pendingSheetMetadataRefreshRequestIds: Set<string> = new Set();
    private findRequestSeq: number = 0;
    private viewportSeq: number = 0;
    private latestViewportSeqSent: number = 0;
    private queuedViewport: { x: number; y: number; width: number; height: number } | null = null;
    private viewportRafId: number | null = null;
    private queuedScroll: { x: number; y: number } | null = null;
    private scrollRafId: number | null = null;
    private lastSentScroll: { x: number; y: number } | null = null;
    private gridDimensions: { cols: number; rows: number } = { cols: 26, rows: 1000 };
    private activeSheetId: number | null = 1;
    private pinnedSheetId: number | null = null;
    private knownSheets: SpreadsheetWorkerSheetListEntry[] = [{ id: 1, name: 'Sheet1' }];
    private frozenPanesBySheetId: Map<number, { rows: number; cols: number }> = new Map();
    private styleSidecars: Map<string, CellFormat> = new Map();
    private onAcceptedOperations: ((operations: SpreadsheetEngineOperationEnvelope[]) => void) | null = null;
    private onUndoableMutation: ((command: UnifiedUndoCommand) => void) | null = null;
    private onSheetSwitched: ((sheetId: number | null) => void) | null = null;
    private recalcSchedulerToken: number = 0;
    private recalcStepInFlight: boolean = false;
    private recalcPendingAfterInFlight: boolean = false;
    private recalcRequestIdInFlight: string | null = null;
    private recalcNextStepTimerId: ReturnType<typeof setTimeout> | null = null;
    private lastSpreadsheetOperationCreatedAtMs: number = 0;


    constructor(bus: WorkerBus, options?: ViewportStreamerOptions) {
        this.bus = bus;
        this.options = {
            forceSpreadsheetViewport: Boolean(options?.forceSpreadsheetViewport),
            allowQueryTabEdits: Boolean(options?.allowQueryTabEdits),
            transformCellInput: options?.transformCellInput,
            transformPastedText: options?.transformPastedText,
        };

        // Subscribe to messages via WorkerBus
        this.unsubscribers.push(this.bus.subscribe('sheet.viewport', (payload) => {
            if (!isSpreadsheetViewportResponsePayload(payload)) return;
            const seq = payload.seq;
            // Ignore stale viewport responses while the user is scrolling quickly.
            if (typeof seq === 'number' && this.latestViewportSeqSent !== 0 && seq !== this.latestViewportSeqSent) {
                return;
            }
            this.handleDataChunk(payload);
        }));

        this.unsubscribers.push(this.bus.subscribe('sheet.range', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const pending = this.pendingRequests.get(requestId);
            const pendingCells = this.pendingRangeCellsRequests.get(requestId);
            if (!pending && !pendingCells) return;
            if (!isSpreadsheetRangeResponsePayload(payload)) {
                if (pending) pending.reject(new Error('Malformed spreadsheet range response payload'));
                if (pendingCells) pendingCells.reject(new Error('Malformed spreadsheet range response payload'));
                if (pending) this.pendingRequests.delete(requestId);
                if (pendingCells) this.pendingRangeCellsRequests.delete(requestId);
                return;
            }
            if (pending) {
                const rows = payload.range.rows.map((row: SpreadsheetRangeCell[]) => (
                    row.map((cell) => cell.raw_input)
                ));
                pending.resolve(rows);
                this.pendingRequests.delete(requestId);
            }
            if (pendingCells) {
                pendingCells.resolve(payload.range.rows);
                this.pendingRangeCellsRequests.delete(requestId);
            }
        }));

        this.unsubscribers.push(this.bus.subscribe('sheet.copyPayload', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const pending = this.pendingCopyRequests.get(requestId);
            if (!pending) return;
            if (!isSpreadsheetCopyPayloadResponsePayload(payload)) {
                pending.reject(new Error('Malformed spreadsheet copy response payload'));
                this.pendingCopyRequests.delete(requestId);
                return;
            }
            this.lastEngineClipboard = payload.clipboard;
            pending.resolve(payload.clipboard.tsv);
            this.pendingCopyRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.namedRanges', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetNamedRangesResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingNamedRangesRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet named ranges response payload'));
                this.pendingNamedRangesRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.namedRangesCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingNamedRangesRequests.get(requestId);
            try { this.namedRangesCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingNamedRangesRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.definedNames', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetDefinedNamesResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingDefinedNamesRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet defined names response payload'));
                this.pendingDefinedNamesRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.definedNamesCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingDefinedNamesRequests.get(requestId);
            try { this.definedNamesCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingDefinedNamesRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('formula.draftEvaluation', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const pending = this.pendingDraftFormulaRequests.get(requestId);
            if (!pending) return;
            if (!isSpreadsheetEvaluateDraftFormulaResponsePayload(payload)) {
                pending.reject(new Error('Malformed draft formula evaluation response payload'));
                this.pendingDraftFormulaRequests.delete(requestId);
                return;
            }
            pending.resolve(payload);
            this.pendingDraftFormulaRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.validationRules', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetValidationRulesResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingValidationRulesRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet validation rules response payload'));
                this.pendingValidationRulesRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.validationRulesCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingValidationRulesRequests.get(requestId);
            try { this.validationRulesCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingValidationRulesRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.conditionalFormatRules', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetConditionalFormatRulesResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingConditionalFormatRulesRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet conditional format rules response payload'));
                this.pendingConditionalFormatRulesRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.conditionalFormatRulesCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingConditionalFormatRulesRequests.get(requestId);
            try { this.conditionalFormatRulesCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingConditionalFormatRulesRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.sparklineGroups', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetSparklineGroupsResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingSparklineGroupsRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet sparkline groups response payload'));
                this.pendingSparklineGroupsRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.sparklineGroupsCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingSparklineGroupsRequests.get(requestId);
            try { this.sparklineGroupsCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingSparklineGroupsRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('workbook.derivedArtifacts', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!isSpreadsheetDerivedArtifactsResponsePayload(payload)) {
                if (!requestId) return;
                const pending = this.pendingDerivedArtifactsRequests.get(requestId);
                if (!pending) return;
                pending.reject(new Error('Malformed spreadsheet derived artifacts response payload'));
                this.pendingDerivedArtifactsRequests.delete(requestId);
                return;
            }
            if (!requestId) {
                try { this.derivedArtifactsCallback?.(payload); } catch { }
                return;
            }
            const pending = this.pendingDerivedArtifactsRequests.get(requestId);
            try { this.derivedArtifactsCallback?.(payload); } catch { }
            if (!pending) return;
            pending.resolve(payload);
            this.pendingDerivedArtifactsRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('sheet.validationDropdownOptions', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const pending = this.pendingValidationDropdownOptionsRequests.get(requestId);
            if (!pending) return;
            if (!isSpreadsheetValidationDropdownOptionsResponsePayload(payload)) {
                pending.reject(new Error('Malformed spreadsheet validation dropdown options response payload'));
                this.pendingValidationDropdownOptionsRequests.delete(requestId);
                return;
            }
            pending.resolve(payload);
            this.pendingValidationDropdownOptionsRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('sheet.validationIssues', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const pending = this.pendingValidationIssuesRequests.get(requestId);
            if (!pending) return;
            if (!isSpreadsheetValidationIssuesResponsePayload(payload)) {
                pending.reject(new Error('Malformed spreadsheet validation issues response payload'));
                this.pendingValidationIssuesRequests.delete(requestId);
                return;
            }
            pending.resolve(payload);
            this.pendingValidationIssuesRequests.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('error', (payload) => {
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            if (!requestId) return;
            const message = isRecord(payload)
                ? String(payload.message ?? 'Spreadsheet worker request failed')
                : 'Spreadsheet worker request failed';
            const structuredError = parseSpreadsheetStructuredError(message);
            const validationIssue = isRecord(payload) && isSpreadsheetValidationIssue(payload.validation_issue)
                ? payload.validation_issue
                : structuredError?.validationIssue;
            const protectedCell = isRecord(payload) && isSpreadsheetCellAddressLike(payload.protected_cell)
                ? payload.protected_cell
                : structuredError?.protectedCell;
            const displayMessage = structuredError?.message ?? message;
            const error = new Error(displayMessage);
            const pendingRange = this.pendingRequests.get(requestId);
            if (pendingRange) {
                pendingRange.reject(error);
                this.pendingRequests.delete(requestId);
            }
            const pendingRangeCells = this.pendingRangeCellsRequests.get(requestId);
            if (pendingRangeCells) {
                pendingRangeCells.reject(error);
                this.pendingRangeCellsRequests.delete(requestId);
            }
            const pendingCopy = this.pendingCopyRequests.get(requestId);
            if (pendingCopy) {
                pendingCopy.reject(error);
                this.pendingCopyRequests.delete(requestId);
            }
            const pendingNamedRanges = this.pendingNamedRangesRequests.get(requestId);
            if (pendingNamedRanges) {
                pendingNamedRanges.reject(error);
                this.pendingNamedRangesRequests.delete(requestId);
            }
            const pendingDefinedNames = this.pendingDefinedNamesRequests.get(requestId);
            if (pendingDefinedNames) {
                pendingDefinedNames.reject(error);
                this.pendingDefinedNamesRequests.delete(requestId);
            }
            const pendingDraftFormula = this.pendingDraftFormulaRequests.get(requestId);
            if (pendingDraftFormula) {
                pendingDraftFormula.reject(error);
                this.pendingDraftFormulaRequests.delete(requestId);
            }
            const pendingValidationRules = this.pendingValidationRulesRequests.get(requestId);
            if (pendingValidationRules) {
                pendingValidationRules.reject(error);
                this.pendingValidationRulesRequests.delete(requestId);
            }
            const pendingValidationDropdownOptions = this.pendingValidationDropdownOptionsRequests.get(requestId);
            if (pendingValidationDropdownOptions) {
                pendingValidationDropdownOptions.reject(error);
                this.pendingValidationDropdownOptionsRequests.delete(requestId);
            }
            const pendingValidationIssues = this.pendingValidationIssuesRequests.get(requestId);
            if (pendingValidationIssues) {
                pendingValidationIssues.reject(error);
                this.pendingValidationIssuesRequests.delete(requestId);
            }
            const pendingValidationOverride = this.pendingValidationOverrideMutations.get(requestId);
            let mutationErrorEmitted = false;
            if (pendingValidationOverride) {
                this.pendingValidationOverrideMutations.delete(requestId);
                try {
                    this.mutationErrorCallback?.({
                        requestId,
                        message: humanizeSpreadsheetMutationError(displayMessage),
                        col: protectedCell?.col ?? validationIssue?.address?.col ?? pendingValidationOverride.col,
                        row: protectedCell?.row ?? validationIssue?.address?.row ?? pendingValidationOverride.row,
                        targetRange: pendingValidationOverride.targetRange,
                        operationLabel: pendingValidationOverride.operationLabel,
                        validationIssue,
                        protectedCell,
                        retryWithValidationOverride: pendingValidationOverride.retryWithValidationOverride,
                    });
                    mutationErrorEmitted = true;
                } catch { }
                this.notifyProjectionChanged();
                if (this.viewportUpdater) this.viewportUpdater();
            }
            const pendingOptimisticEdit = this.pendingOptimisticCellEdits.get(requestId);
            if (pendingOptimisticEdit) {
                this.pendingOptimisticCellEdits.delete(requestId);
                const key = `${pendingOptimisticEdit.col},${pendingOptimisticEdit.row}`;
                if (pendingOptimisticEdit.previous) {
                    this.cellCache.set(key, pendingOptimisticEdit.previous);
                } else {
                    this.cellCache.delete(key);
                }
                try {
                    this.mutationErrorCallback?.({
                        requestId,
                        message: humanizeSpreadsheetMutationError(displayMessage),
                        col: protectedCell?.col ?? validationIssue?.address?.col ?? pendingOptimisticEdit.col,
                        row: protectedCell?.row ?? validationIssue?.address?.row ?? pendingOptimisticEdit.row,
                        validationIssue,
                        protectedCell,
                        value: pendingOptimisticEdit.value,
                    });
                    mutationErrorEmitted = true;
                } catch { }
                this.notifyProjectionChanged();
                if (this.viewportUpdater) this.viewportUpdater();
            }
            const pendingUndo = this.pendingUndoableMutationsByRequestId.get(requestId);
            if (pendingUndo && !mutationErrorEmitted && protectedCell) {
                try {
                    this.mutationErrorCallback?.({
                        requestId,
                        message: humanizeSpreadsheetMutationError(displayMessage),
                        col: protectedCell.col,
                        row: protectedCell.row,
                        operationLabel: pendingUndo.label,
                        protectedCell,
                    });
                } catch { }
                this.notifyProjectionChanged();
                if (this.viewportUpdater) this.viewportUpdater();
            }
            if (pendingUndo) this.clearPendingUndoableMutation(pendingUndo);
            this.pendingAcceptedOperationPublicationRequestIds.delete(requestId);
        }));

        this.unsubscribers.push(this.bus.subscribe('sheet.list', (payload) => {
            if (!isSpreadsheetSheetListResponsePayload(payload)) return;
            this.registerStyleSidecars(payload.styles);
            this.knownSheets = this.normalizeSheetList(payload.sheets);
            for (const sheet of this.knownSheets) {
                const id = Math.floor(Number(sheet.id));
                if (!Number.isFinite(id) || id <= 0) continue;
                const panes = sheet.frozen_panes;
                if (panes) {
                    this.frozenPanesBySheetId.set(id, {
                        rows: Math.max(0, Math.floor(Number(panes.rows) || 0)),
                        cols: Math.max(0, Math.floor(Number(panes.cols) || 0)),
                    });
                } else {
                    this.frozenPanesBySheetId.delete(id);
                }
            }
            if (this.sheetsCallback) this.sheetsCallback(this.knownSheets);
        }));

        const refreshAfterWorkerEdit = () => {
            // A worker-side edit finished (e.g., IDB-backed formula evaluation).
            // Force a viewport refresh so the computed value is pulled into the cache.
            this.lastViewport = null;
            this.cellCache.clear();
            this.notifyProjectionChanged();
            if (this.viewportUpdater) this.viewportUpdater();
        };

        this.unsubscribers.push(this.bus.subscribe('sheet.delta', (payload) => {
            if (!isSpreadsheetDeltaResponsePayload(payload)) return;
            this.handleRecalcDelta(payload);
            const requestId = getSpreadsheetRoutedResponseRequestId(payload);
            const shouldPublishAcceptedOperations = requestId
                ? this.pendingAcceptedOperationPublicationRequestIds.delete(requestId)
                : false;
            const shouldRefreshSheetMetadata = requestId
                ? this.pendingSheetMetadataRefreshRequestIds.delete(requestId)
                : false;
            if (requestId) {
                this.pendingOptimisticCellEdits.delete(requestId);
                this.pendingValidationOverrideMutations.delete(requestId);
            }
            if (Array.isArray(payload.accepted_operations) && payload.accepted_operations.length > 0) {
                if (shouldPublishAcceptedOperations) {
                    try { this.onAcceptedOperations?.(payload.accepted_operations); } catch { }
                }
                this.emitUndoableMutationsForAcceptedOperations(payload.accepted_operations);
            }
            refreshAfterWorkerEdit();
            if (shouldRefreshSheetMetadata) this.getSheets();
        }));
    }

    dispose() {
        if (this.viewportRafId !== null) {
            cancelAnimationFrame(this.viewportRafId);
            this.viewportRafId = null;
        }
        if (this.scrollRafId !== null) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = null;
        }
        this.cancelScheduledRecalcStep();
        for (const unsub of this.unsubscribers.splice(0, this.unsubscribers.length)) {
            try { unsub(); } catch { }
        }
        this.rejectPendingRangeRequests(new Error('ViewportStreamer disposed before spreadsheet range response arrived'));
        this.rejectPendingCopyRequests(new Error('ViewportStreamer disposed before spreadsheet copy response arrived'));
        this.rejectPendingNamedRangesRequests(new Error('ViewportStreamer disposed before spreadsheet named ranges response arrived'));
        this.rejectPendingDefinedNamesRequests(new Error('ViewportStreamer disposed before spreadsheet defined names response arrived'));
        this.rejectPendingDraftFormulaRequests(new Error('ViewportStreamer disposed before draft formula evaluation response arrived'));
        this.rejectPendingValidationRulesRequests(new Error('ViewportStreamer disposed before spreadsheet validation rules response arrived'));
        this.rejectPendingConditionalFormatRulesRequests(new Error('ViewportStreamer disposed before spreadsheet conditional format rules response arrived'));
        this.rejectPendingSparklineGroupsRequests(new Error('ViewportStreamer disposed before spreadsheet sparkline groups response arrived'));
        this.rejectPendingDerivedArtifactsRequests(new Error('ViewportStreamer disposed before spreadsheet derived artifacts response arrived'));
        this.rejectPendingValidationDropdownOptionsRequests(new Error('ViewportStreamer disposed before spreadsheet validation dropdown options response arrived'));
        this.rejectPendingValidationIssuesRequests(new Error('ViewportStreamer disposed before spreadsheet validation issues response arrived'));
        this.pendingUndoableMutationsByRequestId.clear();
        this.pendingUndoableMutationsByOperationId.clear();
        this.pendingAcceptedOperationPublicationRequestIds.clear();
        this.pendingSheetMetadataRefreshRequestIds.clear();
        this.pendingOptimisticCellEdits.clear();
        this.pendingValidationOverrideMutations.clear();
        this.cellCache.clear();
        this.callback = null;
        this.projectionRefreshCallback = null;
        this.viewportUpdater = null;
        this.sheetsCallback = null;
        this.namedRangesCallback = null;
        this.definedNamesCallback = null;
        this.validationRulesCallback = null;
        this.conditionalFormatRulesCallback = null;
        this.sparklineGroupsCallback = null;
        this.derivedArtifactsCallback = null;
        this.mutationErrorCallback = null;
        this.findCallback = null;
        this.onAcceptedOperations = null;
        this.onUndoableMutation = null;
        this.onSheetSwitched = null;
    }



    setCallback(cb: () => void) {
        this.callback = cb;
    }

    setProjectionRefreshCallback(cb: (() => void) | null) {
        this.projectionRefreshCallback = cb;
    }

    setViewportUpdater(updater: () => void) {
        this.viewportUpdater = updater;
    }

    setSheetsCallback(cb: (sheets: SpreadsheetWorkerSheetListEntry[]) => void) {
        this.sheetsCallback = cb;
    }

    setNamedRangesCallback(cb: ((payload: SpreadsheetNamedRangesResponsePayload) => void) | null) {
        this.namedRangesCallback = cb;
    }

    setDefinedNamesCallback(cb: ((payload: SpreadsheetDefinedNamesResponsePayload) => void) | null) {
        this.definedNamesCallback = cb;
    }

    setValidationRulesCallback(cb: ((payload: SpreadsheetValidationRulesResponsePayload) => void) | null) {
        this.validationRulesCallback = cb;
    }

    setConditionalFormatRulesCallback(cb: ((payload: SpreadsheetConditionalFormatRulesResponsePayload) => void) | null) {
        this.conditionalFormatRulesCallback = cb;
    }

    setSparklineGroupsCallback(cb: ((payload: SpreadsheetSparklineGroupsResponsePayload) => void) | null) {
        this.sparklineGroupsCallback = cb;
    }

    setDerivedArtifactsCallback(cb: ((payload: SpreadsheetDerivedArtifactsResponsePayload) => void) | null) {
        this.derivedArtifactsCallback = cb;
    }

    setMutationErrorCallback(cb: ((error: SpreadsheetMutationError) => void) | null) {
        this.mutationErrorCallback = cb;
    }

    setFindCallback(cb: (matches: SpreadsheetFindMatch[]) => void) {
        this.findCallback = cb;
    }

    private notifyProjectionChanged() {
        if (this.callback) this.callback();
        if (this.projectionRefreshCallback) this.projectionRefreshCallback();
    }

    getSheets() {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const command = buildSpreadsheetWorkerCommand({
            command: 'sheet.list',
            requestId: Math.random().toString(36).substring(7),
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        this.bus.sendCommand(command.command, command);
    }

    getNamedRanges(): Promise<SpreadsheetNamedRangesResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getNamedRanges',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetNamedRangesResponsePayload>((resolve, reject) => {
            this.pendingNamedRangesRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getDefinedNames(): Promise<SpreadsheetDefinedNamesResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getDefinedNames',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetDefinedNamesResponsePayload>((resolve, reject) => {
            this.pendingDefinedNamesRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    evaluateDraftFormula(
        formula: string,
        opts?: { sheetId?: number | null; engineSheetId?: string | null },
    ): Promise<SpreadsheetEvaluateDraftFormulaResponsePayload> {
        const sheetId = String(opts?.engineSheetId || buildSpreadsheetEngineSheetId(opts?.sheetId ?? this.pinnedSheetId ?? this.activeSheetId));
        const requestId = Math.random().toString(36).substring(7);
        const payload: SpreadsheetEvaluateDraftFormulaPayload = {
            sheet_id: sheetId,
            formula: String(formula ?? ''),
        };
        const command = buildSpreadsheetWorkerCommand<SpreadsheetEvaluateDraftFormulaPayload>({
            command: 'formula.evaluateDraft',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        const promise = new Promise<SpreadsheetEvaluateDraftFormulaResponsePayload>((resolve, reject) => {
            this.pendingDraftFormulaRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getValidationRules(): Promise<SpreadsheetValidationRulesResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getValidationRules',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetValidationRulesResponsePayload>((resolve, reject) => {
            this.pendingValidationRulesRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getConditionalFormatRules(): Promise<SpreadsheetConditionalFormatRulesResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getConditionalFormatRules',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetConditionalFormatRulesResponsePayload>((resolve, reject) => {
            this.pendingConditionalFormatRulesRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getSparklineGroups(): Promise<SpreadsheetSparklineGroupsResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getSparklineGroups',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetSparklineGroupsResponsePayload>((resolve, reject) => {
            this.pendingSparklineGroupsRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getDerivedArtifacts(): Promise<SpreadsheetDerivedArtifactsResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: 'workbook.getDerivedArtifacts',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        const promise = new Promise<SpreadsheetDerivedArtifactsResponsePayload>((resolve, reject) => {
            this.pendingDerivedArtifactsRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getValidationDropdownOptions(address: SpreadsheetCellAddress): Promise<SpreadsheetValidationDropdownOptionsResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const payload: SpreadsheetValidationDropdownOptionsPayload = {
            address: {
                sheet_id: String(address?.sheet_id || sheetId),
                row: Math.max(0, Math.floor(Number(address?.row ?? 0))),
                col: Math.max(0, Math.floor(Number(address?.col ?? 0))),
            },
        };
        const command = buildSpreadsheetWorkerCommand<SpreadsheetValidationDropdownOptionsPayload>({
            command: 'sheet.getValidationDropdownOptions',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        const promise = new Promise<SpreadsheetValidationDropdownOptionsResponsePayload>((resolve, reject) => {
            this.pendingValidationDropdownOptionsRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getValidationIssues(range?: SpreadsheetRangeAddress | null): Promise<SpreadsheetValidationIssuesResponsePayload> {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const payload: SpreadsheetValidationIssuesPayload = {};
        if (range) {
            payload.range = normalizeSpreadsheetRange({
                ...range,
                sheet_id: String(range.sheet_id || sheetId),
            });
        }
        const command = buildSpreadsheetWorkerCommand<SpreadsheetValidationIssuesPayload>({
            command: 'sheet.getValidationIssues',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        const promise = new Promise<SpreadsheetValidationIssuesResponsePayload>((resolve, reject) => {
            this.pendingValidationIssuesRequests.set(requestId, { resolve, reject });
        });
        this.bus.sendCommand(command.command, command);
        return promise;
    }

    getActiveSheetId(): number | null {
        return this.pinnedSheetId ?? this.activeSheetId;
    }

    getWorkerActiveSheetId(): number | null {
        return this.activeSheetId;
    }

    setPinnedSheetId(sheetId: number | null) {
        const previousVisibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        const idNum = Number(sheetId);
        if (Number.isFinite(idNum) && idNum > 0) {
            this.pinnedSheetId = Math.floor(idNum);
        } else {
            this.pinnedSheetId = null;
        }
        const nextVisibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        if (previousVisibleSheetId !== nextVisibleSheetId) {
            this.invalidateVisibleSheetState();
        }
    }

    setAcceptedOperationsCallback(cb: ((operations: SpreadsheetEngineOperationEnvelope[]) => void) | null) {
        this.onAcceptedOperations = cb;
    }

    setUndoableMutationCallback(cb: ((command: UnifiedUndoCommand) => void) | null) {
        this.onUndoableMutation = cb;
    }

    setSheetSwitchedCallback(cb: ((sheetId: number | null) => void) | null) {
        this.onSheetSwitched = cb;
    }

    setSelection(col: number, row: number, endCol?: number, endRow?: number) {
        void col;
        void row;
        void endCol;
        void endRow;
    }

    setScroll(x: number, y: number) {
        // Throttle to keep mobile scrolling responsive (avoid flooding worker with messages)
        this.queuedScroll = { x, y };
        if (this.scrollRafId !== null) return;

        this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            const next = this.queuedScroll;
            this.queuedScroll = null;
            if (!next) return;

            if (this.lastSentScroll && this.lastSentScroll.x === next.x && this.lastSentScroll.y === next.y) {
                return;
            }

            this.lastSentScroll = next;
        });
    }

    private nextSpreadsheetOperationCreatedAtMs(): number {
        const now = Date.now();
        const next = Math.max(now, this.lastSpreadsheetOperationCreatedAtMs + 1);
        this.lastSpreadsheetOperationCreatedAtMs = next;
        return next;
    }

    setViewport(width: number, height: number) {
        void width;
        void height;
    }

    setDimensions(cols: number, rows: number) {
        const nextCols = Math.max(1, Math.floor(Number(cols) || 1));
        const nextRows = Math.max(1, Math.floor(Number(rows) || 1));
        if (this.gridDimensions.cols === nextCols && this.gridDimensions.rows === nextRows) return;
        this.gridDimensions = { cols: nextCols, rows: nextRows };
        const vp = this.lastViewport;
        if (vp) {
            this.lastViewport = null;
            this.updateViewport(vp.x, vp.y, vp.width, vp.height);
        }
    }

    addSheet(name: string) {
        const requestId = Math.random().toString(36).substring(7);
        const nextNumericId = this.nextSheetNumber();
        const sheetId = buildSpreadsheetEngineSheetId(nextNumericId);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_add_sheet_${Date.now()}_${requestId}`,
            op_type: 'AddSheet',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                name: String(name ?? '').trim() || `Sheet${nextNumericId}`,
            },
        });
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: [operation],
                allowQueryTabEdit: this.options.allowQueryTabEdits,
                returnAcceptedOperations: true,
            },
        });
        this.bus.sendCommand(command.command, command);
    }

    switchSheet(id: number) {
        const previousVisibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        if (Number.isFinite(Number(id)) && Number(id) > 0) {
            this.activeSheetId = Math.floor(Number(id));
        }
        const nextVisibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        if (previousVisibleSheetId !== nextVisibleSheetId) {
            this.invalidateVisibleSheetState();
        }
        try { if (this.onSheetSwitched) this.onSheetSwitched(this.activeSheetId); } catch { }
    }

    async findText(query: string) {
        const search = String(query ?? '').trim().toLowerCase();
        const requestSeq = ++this.findRequestSeq;
        if (!search) {
            if (this.findCallback) this.findCallback([]);
            return;
        }
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        let rows: string[][];
        try {
            rows = await this.getRangeData(0, 0, maxCol, maxRow);
        } catch {
            if (requestSeq === this.findRequestSeq && this.findCallback) this.findCallback([]);
            return;
        }
        if (requestSeq !== this.findRequestSeq) return;

        const matches: SpreadsheetFindMatch[] = [];
        for (let row = 0; row < rows.length; row += 1) {
            const cells = rows[row] ?? [];
            for (let col = 0; col < cells.length; col += 1) {
                const value = String(cells[col] ?? '');
                if (value.toLowerCase().includes(search)) {
                    matches.push({
                        col,
                        row,
                        data: { value },
                    });
                }
            }
        }
        if (this.findCallback) this.findCallback(matches);
    }

    replaceText(
        query: string,
        replacement: string,
        opts?: {
            range?: { startCol: number; startRow: number; endCol: number; endRow: number };
            matchCase?: boolean;
            wholeCell?: boolean;
            validationOverride?: boolean;
        },
    ): boolean {
        const findQuery = String(query ?? '');
        if (findQuery.length === 0) return false;
        const sheetId = this.engineSheetIdForUiSheet();
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const sourceRange = opts?.range;
        const startCol = Math.max(0, Math.floor(Number(sourceRange?.startCol ?? 0) || 0));
        const startRow = Math.max(0, Math.floor(Number(sourceRange?.startRow ?? 0) || 0));
        const endCol = Math.max(startCol, Math.min(maxCol, Math.floor(Number(sourceRange?.endCol ?? maxCol) || 0)));
        const endRow = Math.max(startRow, Math.min(maxRow, Math.floor(Number(sourceRange?.endRow ?? maxRow) || 0)));
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_find_replace_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetFindReplacePayload>({
            command: 'sheet.findReplace',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
                query: findQuery,
                replacement: String(replacement ?? ''),
                match_case: opts?.matchCase === true,
                whole_cell: opts?.wholeCell === true,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            startCol,
            startRow,
            () => this.replaceText(findQuery, replacement, { ...opts, validationOverride: true }),
            opts?.validationOverride !== true,
            {
                operationLabel: 'Find and replace',
                targetRange: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Find and replace',
                operationIds: [opId],
            },
        });
        this.cellCache.clear();
        return true;
    }

    removeDuplicates(
        range: { startCol: number; startRow: number; endCol: number; endRow: number },
        opts?: {
            keyColumns?: number[];
            hasHeader?: boolean;
            validationOverride?: boolean;
        },
    ): boolean {
        const sheetId = this.engineSheetIdForUiSheet();
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const startCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.startCol) || 0)));
        const startRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.startRow) || 0)));
        const endCol = Math.max(startCol, Math.min(maxCol, Math.floor(Number(range.endCol) || startCol)));
        const endRow = Math.max(startRow, Math.min(maxRow, Math.floor(Number(range.endRow) || startRow)));
        if (endRow <= startRow && opts?.hasHeader) return false;
        const keyColumns = Array.isArray(opts?.keyColumns)
            ? opts!.keyColumns!
                .map((value) => Math.floor(Number(value)))
                .filter((value) => Number.isFinite(value) && value >= startCol && value <= endCol)
            : [];
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_remove_duplicates_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetRemoveDuplicatesPayload>({
            command: 'sheet.removeDuplicates',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
                key_columns: keyColumns,
                has_header: opts?.hasHeader === true,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            startCol,
            startRow,
            () => this.removeDuplicates({ startCol, startRow, endCol, endRow }, { ...opts, validationOverride: true }),
            opts?.validationOverride !== true,
            {
                operationLabel: 'Remove duplicates',
                targetRange: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Remove duplicates',
                operationIds: [opId],
            },
        });
        this.cellCache.clear();
        return true;
    }

    sortRange(
        range: { startCol: number; startRow: number; endCol: number; endRow: number },
        opts?: {
            sortColumns?: Array<{ col: number; descending?: boolean }>;
            hasHeader?: boolean;
            validationOverride?: boolean;
        },
    ): boolean {
        const sheetId = this.engineSheetIdForUiSheet();
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const startCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.startCol) || 0)));
        const startRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.startRow) || 0)));
        const endCol = Math.max(startCol, Math.min(maxCol, Math.floor(Number(range.endCol) || startCol)));
        const endRow = Math.max(startRow, Math.min(maxRow, Math.floor(Number(range.endRow) || startRow)));
        if (endRow <= startRow && opts?.hasHeader) return false;
        const sortColumns = Array.isArray(opts?.sortColumns)
            ? opts!.sortColumns!
                .map((column) => ({
                    col: Math.floor(Number(column?.col)),
                    descending: column?.descending === true,
                }))
                .filter((column) => Number.isFinite(column.col) && column.col >= startCol && column.col <= endCol)
            : [];
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const clearOpId = `viewport_streamer_sort_clear_${now}_${requestId}`;
        const setOpId = `viewport_streamer_sort_set_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetSortRangePayload>({
            command: 'sheet.sortRange',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
                sort_columns: sortColumns,
                has_header: opts?.hasHeader === true,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                clear_op_id: clearOpId,
                set_op_id: setOpId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            startCol,
            startRow,
            () => this.sortRange({ startCol, startRow, endCol, endRow }, { ...opts, validationOverride: true }),
            opts?.validationOverride !== true,
            {
                operationLabel: 'Sort range',
                targetRange: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: endRow,
                    end_col: endCol,
                },
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Sort range',
                operationIds: [clearOpId, setOpId],
            },
        });
        this.cellCache.clear();
        return true;
    }

    setAutoFilter(
        range: { startCol: number; startRow: number; endCol: number; endRow: number } | null,
        columns?: SpreadsheetAutoFilterColumnCriteria[],
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetAutoFilterPayload> {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const normalizedRange = range
            ? {
                sheet_id: sheetId,
                start_row: Math.max(0, Math.min(maxRow, Math.floor(Number(range.startRow) || 0))),
                start_col: Math.max(0, Math.min(maxCol, Math.floor(Number(range.startCol) || 0))),
                end_row: Math.max(0, Math.min(maxRow, Math.floor(Number(range.endRow) || 0))),
                end_col: Math.max(0, Math.min(maxCol, Math.floor(Number(range.endCol) || 0))),
            }
            : null;
        if (normalizedRange) {
            const startRow = Math.min(normalizedRange.start_row, normalizedRange.end_row);
            const endRow = Math.max(normalizedRange.start_row, normalizedRange.end_row);
            const startCol = Math.min(normalizedRange.start_col, normalizedRange.end_col);
            const endCol = Math.max(normalizedRange.start_col, normalizedRange.end_col);
            normalizedRange.start_row = startRow;
            normalizedRange.end_row = endRow;
            normalizedRange.start_col = startCol;
            normalizedRange.end_col = endCol;
        }
        const normalizedColumns = Array.isArray(columns)
            ? columns
                .map((column) => ({
                    col: Math.floor(Number(column?.col)),
                    values: Array.isArray(column?.values)
                        ? column.values.map((value) => String(value ?? '').trim()).filter(Boolean)
                        : [],
                    blank: column?.blank === true,
                    conditions: Array.isArray(column?.conditions)
                        ? column.conditions
                            .map((condition): SpreadsheetAutoFilterCondition | null => {
                                const value = String(condition?.value ?? '').trim();
                                if (!value) return null;
                                return {
                                    operator: condition.operator,
                                    value,
                                };
                            })
                            .filter((condition): condition is SpreadsheetAutoFilterCondition => condition !== null)
                        : [],
                    condition_join: column?.condition_join === 'or' ? 'or' as const : 'and' as const,
                }))
                .filter((column) => (
                    Number.isFinite(column.col) &&
                    (!normalizedRange || (column.col >= normalizedRange.start_col && column.col <= normalizedRange.end_col)) &&
                    (column.blank || column.values.length > 0 || column.conditions.length > 0)
                ))
                .map((column) => ({
                    col: column.col,
                    values: column.values,
                    blank: column.blank,
                    ...(column.conditions.length > 0 ? {
                        conditions: column.conditions,
                        condition_join: column.condition_join,
                    } : {}),
                }))
            : [];
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetAutoFilterPayload>({
            op_id: `viewport_streamer_SetAutoFilter_${Date.now()}_${requestId}`,
            op_type: 'SetAutoFilter',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                ...(normalizedRange ? { range: normalizedRange, columns: normalizedColumns } : {}),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: normalizedRange ? 'Filter range' : 'Clear filter',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        void this.getSheets();
        this.notifyProjectionChanged();
        return operation;
    }

    upsertTable(
        range: { startCol: number; startRow: number; endCol: number; endRow: number },
        opts?: {
            sheetId?: number | null;
            tableId?: string | null;
            name?: string | null;
            hasHeaders?: boolean;
            showFilterButtons?: boolean;
            showTotals?: boolean;
            styleId?: string | null;
            columns?: SpreadsheetTableDefinition['columns'];
            skipUndoRegistration?: boolean;
            label?: string;
        },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetUpsertTablePayload> {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const startCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.startCol) || 0)));
        const startRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.startRow) || 0)));
        const endCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.endCol) || 0)));
        const endRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.endRow) || 0)));
        const normalizedRange = {
            sheet_id: sheetId,
            start_row: Math.min(startRow, endRow),
            start_col: Math.min(startCol, endCol),
            end_row: Math.max(startRow, endRow),
            end_col: Math.max(startCol, endCol),
        };
        const tableId = String(opts?.tableId ?? `table_${Date.now()}_${requestId}`).trim();
        const name = String(opts?.name ?? `Table${Date.now()}`).trim();
        const table: SpreadsheetTableDefinition = {
            id: tableId,
            name,
            range: normalizedRange,
            has_headers: opts?.hasHeaders !== false,
            show_filter_buttons: opts?.showFilterButtons !== false,
            show_totals: opts?.showTotals === true,
            ...(String(opts?.styleId ?? '').trim() ? { style_id: String(opts?.styleId).trim() } : {}),
            ...(Array.isArray(opts?.columns) ? { columns: opts.columns } : {}),
        };
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetUpsertTablePayload>({
            op_id: `viewport_streamer_UpsertTable_${Date.now()}_${requestId}`,
            op_type: 'UpsertTable',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { table },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: opts?.label ?? 'Create table',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        void this.getSheets();
        this.notifyProjectionChanged();
        return operation;
    }

    setTableTotalRow(
        table: SpreadsheetTableDefinition,
        showTotals: boolean,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope[] {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const range = table?.range;
        if (!range) return [];
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const startCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.start_col) || 0)));
        const startRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.start_row) || 0)));
        const endCol = Math.max(startCol, Math.min(maxCol, Math.floor(Number(range.end_col) || startCol)));
        const endRow = Math.max(startRow, Math.min(maxRow, Math.floor(Number(range.end_row) || startRow)));
        const currentlyShowingTotals = table.show_totals === true;
        if (showTotals === currentlyShowingTotals) return [];
        if (showTotals && endRow >= maxRow) return [];

        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const nextEndRow = showTotals ? endRow + 1 : Math.max(startRow, endRow - 1);
        const nextRange = {
            sheet_id: sheetId,
            start_row: startRow,
            start_col: startCol,
            end_row: nextEndRow,
            end_col: endCol,
        };
        const nextTable: SpreadsheetTableDefinition = {
            id: String(table.id ?? '').trim(),
            name: String(table.name ?? '').trim(),
            range: nextRange,
            has_headers: table.has_headers !== false,
            show_filter_buttons: table.show_filter_buttons !== false,
            show_totals: showTotals,
            ...(String(table.style_id ?? '').trim() ? { style_id: String(table.style_id).trim() } : {}),
            ...(Array.isArray(table.columns) ? { columns: table.columns.map((column) => ({ ...column })) } : {}),
        };
        const upsertOperation = buildSpreadsheetOperationEnvelope<SpreadsheetUpsertTablePayload>({
            op_id: `viewport_streamer_UpsertTableTotals_${now}_${requestId}`,
            op_type: 'UpsertTable',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: now,
            payload: { table: nextTable },
        });
        const operations: SpreadsheetEngineOperationEnvelope[] = [];
        if (showTotals) {
            operations.push(upsertOperation);
            const columns = Array.isArray(table.columns) ? table.columns : [];
            const width = endCol - startCol + 1;
            const totalInputs = Array.from({ length: width }, (_, offset) => {
                if (offset === 0) return 'Total';
                const columnName = columns[offset]?.name ?? '';
                return structuredReferenceFormulaForColumn(nextTable.name, columnName) ?? '';
            });
            operations.push(buildSpreadsheetOperationEnvelope({
                op_id: `viewport_streamer_SetTableTotals_${now}_${requestId}`,
                op_type: 'SetRangeInput',
                workbook_id: 'active_workbook',
                sheet_id: sheetId,
                base_revision: 0,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                payload: {
                    range: {
                        sheet_id: sheetId,
                        start_row: nextEndRow,
                        start_col: startCol,
                        end_row: nextEndRow,
                        end_col: endCol,
                    },
                    raw_inputs: [totalInputs],
                },
            }));
        } else {
            operations.push(buildSpreadsheetOperationEnvelope({
                op_id: `viewport_streamer_ClearTableTotals_${now}_${requestId}`,
                op_type: 'ClearRange',
                workbook_id: 'active_workbook',
                sheet_id: sheetId,
                base_revision: 0,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                payload: {
                    range: {
                        sheet_id: sheetId,
                        start_row: endRow,
                        start_col: startCol,
                        end_row: endRow,
                        end_col: endCol,
                    },
                },
            }));
            operations.push(upsertOperation);
        }
        const sent = this.sendSpreadsheetEngineOperations(operations, opts?.sheetId, {
            label: showTotals ? 'Show table total row' : 'Hide table total row',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        if (sent) {
            this.cellCache.clear();
            void this.getSheets();
            this.notifyProjectionChanged();
        }
        return sent?.operations ?? [];
    }

    deleteTable(
        tableId: string,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetDeleteTablePayload> {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const normalizedTableId = String(tableId ?? '').trim();
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetDeleteTablePayload>({
            op_id: `viewport_streamer_DeleteTable_${Date.now()}_${requestId}`,
            op_type: 'DeleteTable',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                table_id: normalizedTableId,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: 'Delete table',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        void this.getSheets();
        this.notifyProjectionChanged();
        return operation;
    }

    upsertDerivedArtifact(
        kind: SpreadsheetDerivedArtifactKind,
        range: { startCol: number; startRow: number; endCol: number; endRow: number },
        opts?: {
            sheetId?: number | null;
            artifactId?: string | null;
            title?: string | null;
            chartType?: SpreadsheetDerivedArtifact['chart_type'];
            metadata?: SpreadsheetDerivedArtifact['metadata'];
            skipUndoRegistration?: boolean;
        },
    ): SpreadsheetEngineOperationEnvelope {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const maxCol = Math.max(0, this.gridDimensions.cols - 1);
        const maxRow = Math.max(0, this.gridDimensions.rows - 1);
        const startCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.startCol) || 0)));
        const startRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.startRow) || 0)));
        const endCol = Math.max(0, Math.min(maxCol, Math.floor(Number(range.endCol) || 0)));
        const endRow = Math.max(0, Math.min(maxRow, Math.floor(Number(range.endRow) || 0)));
        const sourceRange: SpreadsheetRangeAddress = {
            sheet_id: sheetId,
            start_row: Math.min(startRow, endRow),
            start_col: Math.min(startCol, endCol),
            end_row: Math.max(startRow, endRow),
            end_col: Math.max(startCol, endCol),
        };
        const placementWidth = kind === 'pivot' ? 6 : 8;
        const placementHeight = kind === 'pivot' ? 14 : 15;
        const placementStartCol = kind === 'pivot'
            ? Math.min(maxCol, sourceRange.end_col + 2)
            : sourceRange.start_col;
        const placementStartRow = kind === 'pivot'
            ? sourceRange.start_row
            : Math.min(maxRow, sourceRange.end_row + 2);
        const placement: SpreadsheetRangeAddress = {
            sheet_id: sheetId,
            start_row: placementStartRow,
            start_col: placementStartCol,
            end_row: Math.min(maxRow, placementStartRow + placementHeight - 1),
            end_col: Math.min(maxCol, placementStartCol + placementWidth - 1),
        };
        const artifactId = String(opts?.artifactId ?? `artifact_${kind}_${Date.now()}_${requestId}`).trim();
        const artifact: SpreadsheetDerivedArtifact = {
            id: artifactId,
            sheet_id: sheetId,
            kind,
            source_range: sourceRange,
            placement,
            ...(String(opts?.title ?? '').trim() ? { title: String(opts?.title).trim() } : {}),
            ...(kind === 'chart' ? { chart_type: opts?.chartType ?? 'column' } : {}),
            ...(opts?.metadata && !Array.isArray(opts.metadata) ? { metadata: opts.metadata } : {}),
            ...(kind === 'pivot' ? { pivot: { row_fields: [], column_fields: [], value_fields: [], filter_fields: [] } } : {}),
        };
        const createdAtMs = this.nextSpreadsheetOperationCreatedAtMs();
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_UpsertDerivedArtifact_${createdAtMs}_${requestId}`,
            op_type: 'UpsertDerivedArtifact',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: createdAtMs,
            payload: { artifact },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: kind === 'pivot' ? 'Create pivot artifact' : 'Create chart artifact',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.notifyProjectionChanged();
        return operation;
    }

    updateDerivedArtifact(
        artifact: SpreadsheetDerivedArtifact,
        opts?: {
            sheetId?: number | null;
            skipUndoRegistration?: boolean;
            label?: string;
        },
    ): SpreadsheetEngineOperationEnvelope {
        const sheetId = String(artifact.sheet_id || this.engineSheetIdForUiSheet(opts?.sheetId));
        const requestId = Math.random().toString(36).substring(7);
        const normalizedArtifact: SpreadsheetDerivedArtifact = {
            ...artifact,
            id: String(artifact.id ?? '').trim(),
            sheet_id: sheetId,
            source_range: { ...artifact.source_range, sheet_id: artifact.source_range?.sheet_id || sheetId },
            placement: { ...artifact.placement, sheet_id: artifact.placement?.sheet_id || sheetId },
        };
        const createdAtMs = this.nextSpreadsheetOperationCreatedAtMs();
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_UpsertDerivedArtifact_${createdAtMs}_${requestId}`,
            op_type: 'UpsertDerivedArtifact',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: createdAtMs,
            payload: { artifact: normalizedArtifact },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: opts?.label ?? 'Update derived artifact',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        return operation;
    }

    deleteDerivedArtifact(
        artifactId: string,
        opts?: {
            sheetId?: number | null;
            skipUndoRegistration?: boolean;
        },
    ): SpreadsheetEngineOperationEnvelope {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const createdAtMs = this.nextSpreadsheetOperationCreatedAtMs();
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_DeleteDerivedArtifact_${createdAtMs}_${requestId}`,
            op_type: 'DeleteDerivedArtifact',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: createdAtMs,
            payload: { artifact_id: String(artifactId ?? '').trim() },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: 'Delete derived artifact',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        return operation;
    }

    moveData(srcCol: number, srcRow: number, width: number, height: number, destCol: number, destRow: number, opts?: { validationOverride?: boolean }) {
        void this.moveDataThroughEngine(srcCol, srcRow, width, height, destCol, destRow, opts);
    }

    copyData(srcCol: number, srcRow: number, width: number, height: number, destCol: number, destRow: number, opts?: { validationOverride?: boolean }) {
        void this.copyDataThroughEngine(srcCol, srcRow, width, height, destCol, destRow, opts);
    }

    fillData(
        srcCol: number,
        srcRow: number,
        srcW: number,
        srcH: number,
        destCol: number,
        destRow: number,
        destW: number,
        destH: number,
        mode: SpreadsheetFillRangeMode = 'auto',
        opts?: { validationOverride?: boolean },
    ) {
        void this.fillDataThroughEngine(srcCol, srcRow, srcW, srcH, destCol, destRow, destW, destH, mode, opts);
    }

    undo() {
        this.sendHistoryCommand('sheet.undo');
        this.cellCache.clear();
        this.notifyProjectionChanged();
    }

    redo() {
        this.sendHistoryCommand('sheet.redo');
        this.cellCache.clear();
        this.notifyProjectionChanged();
    }

    recalc() {
        this.recalcSchedulerToken += 1;
        this.recalcPendingAfterInFlight = false;
        this.cancelScheduledRecalcStep();
        if (this.recalcStepInFlight) {
            this.recalcPendingAfterInFlight = true;
        } else {
            this.sendIncrementalRecalcStep(this.recalcSchedulerToken);
        }
        this.cellCache.clear();
        this.notifyProjectionChanged();
    }

    insertColumns(colIndex: number, count: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('InsertColumns', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            col_index: Math.max(0, Math.floor(Number(colIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
        }, opts);
    }

    deleteColumns(colIndex: number, count: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('DeleteColumns', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            col_index: Math.max(0, Math.floor(Number(colIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
        }, opts);
    }

    insertRows(rowIndex: number, count: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('InsertRows', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            row_index: Math.max(0, Math.floor(Number(rowIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
        }, opts);
    }

    deleteRows(rowIndex: number, count: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('DeleteRows', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            row_index: Math.max(0, Math.floor(Number(rowIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
        }, opts);
    }

    resizeColumns(colIndex: number, count: number, widthPx: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('ResizeColumns', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            col_index: Math.max(0, Math.floor(Number(colIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
            width_px: Math.max(1, Number(widthPx) || 1),
        }, opts);
    }

    resizeRows(rowIndex: number, count: number, heightPx: number, opts?: ProjectionWriteOptions) {
        this.sendStructuralOperation('ResizeRows', {
            sheet_id: this.engineSheetIdForUiSheet(opts?.sheetId),
            row_index: Math.max(0, Math.floor(Number(rowIndex) || 0)),
            count: Math.max(1, Math.floor(Number(count) || 1)),
            height_px: Math.max(1, Number(heightPx) || 1),
        }, opts);
    }

    setFrozenPanes(rows: number, cols: number, opts?: { sheetId?: number | null; skipUndoRegistration?: boolean }): SpreadsheetEngineOperationEnvelope<SpreadsheetSetFrozenPanesPayload> {
        const requestId = Math.random().toString(36).substring(7);
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const normalizedRows = Math.max(0, Math.floor(Number(rows) || 0));
        const normalizedCols = Math.max(0, Math.floor(Number(cols) || 0));
        const payload: SpreadsheetSetFrozenPanesPayload = {
            sheet_id: sheetId,
            rows: normalizedRows,
            cols: normalizedCols,
        };
        const uiSheetId = Math.floor(Number(opts?.sheetId ?? this.pinnedSheetId ?? this.activeSheetId));
        if (Number.isFinite(uiSheetId) && uiSheetId > 0) {
            this.frozenPanesBySheetId.set(uiSheetId, { rows: normalizedRows, cols: normalizedCols });
            this.knownSheets = this.knownSheets.map((sheet) => (
                Math.floor(Number(sheet.id)) === uiSheetId
                    ? { ...sheet, frozen_panes: { rows: normalizedRows, cols: normalizedCols } }
                    : sheet
            ));
        }
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetFrozenPanesPayload>({
            op_id: `viewport_streamer_SetFrozenPanes_${Date.now()}_${requestId}`,
            op_type: 'SetFrozenPanes',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload,
        });
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: [operation],
                allowQueryTabEdit: this.options.allowQueryTabEdits,
                returnAcceptedOperations: true,
            },
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: normalizedRows > 0 || normalizedCols > 0 ? 'Freeze panes' : 'Clear frozen panes',
                operationIds: [operation.op_id],
                skipUndoRegistration: opts?.skipUndoRegistration,
            },
        });
        this.notifyProjectionChanged();
        return operation;
    }

    private handleRecalcDelta(payload: SpreadsheetDeltaResponsePayload) {
        const step = payload.recalc_step;
        if (!step) {
            if (this.recalcRequestIdInFlight && payload.requestId === this.recalcRequestIdInFlight) {
                this.recalcStepInFlight = false;
                this.recalcRequestIdInFlight = null;
            }
            return;
        }

        const token = Number(step.scheduler_token);
        const currentToken = this.recalcSchedulerToken;
        this.recalcStepInFlight = false;
        this.recalcRequestIdInFlight = null;

        if (!Number.isFinite(token) || token !== currentToken) {
            if (this.recalcPendingAfterInFlight) {
                this.recalcPendingAfterInFlight = false;
                this.sendIncrementalRecalcStep(currentToken);
            }
            return;
        }

        if (this.recalcPendingAfterInFlight) {
            this.recalcPendingAfterInFlight = false;
            this.sendIncrementalRecalcStep(currentToken);
            return;
        }

        if (!step.completed) {
            this.scheduleIncrementalRecalcStep(currentToken);
        }
    }

    private sendIncrementalRecalcStep(token: number) {
        if (token !== this.recalcSchedulerToken || this.recalcStepInFlight) return;
        const requestId = Math.random().toString(36).substring(7);
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const command = buildSpreadsheetWorkerCommand<SpreadsheetRecalcIncrementalPayload>({
            command: 'sheet.recalcIncremental',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                maxFormulaCount: 64,
                schedulerToken: String(token),
            },
        });
        this.recalcStepInFlight = true;
        this.recalcRequestIdInFlight = requestId;
        this.bus.sendCommand(command.command, command);
    }

    private scheduleIncrementalRecalcStep(token: number) {
        this.cancelScheduledRecalcStep();
        this.recalcNextStepTimerId = setTimeout(() => {
            this.recalcNextStepTimerId = null;
            this.sendIncrementalRecalcStep(token);
        }, 0);
    }

    private cancelScheduledRecalcStep() {
        if (this.recalcNextStepTimerId === null) return;
        clearTimeout(this.recalcNextStepTimerId);
        this.recalcNextStepTimerId = null;
    }

    private rejectPendingRangeRequests(error: Error) {
        for (const pending of this.pendingRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingRequests.clear();
        for (const pending of this.pendingRangeCellsRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingRangeCellsRequests.clear();
    }

    private rejectPendingCopyRequests(error: Error) {
        for (const pending of this.pendingCopyRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingCopyRequests.clear();
    }

    private rejectPendingNamedRangesRequests(error: Error) {
        for (const pending of this.pendingNamedRangesRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingNamedRangesRequests.clear();
    }

    private rejectPendingDefinedNamesRequests(error: Error) {
        for (const pending of this.pendingDefinedNamesRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingDefinedNamesRequests.clear();
    }

    private rejectPendingDraftFormulaRequests(error: Error) {
        for (const pending of this.pendingDraftFormulaRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingDraftFormulaRequests.clear();
    }

    private rejectPendingValidationRulesRequests(error: Error) {
        for (const pending of this.pendingValidationRulesRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingValidationRulesRequests.clear();
    }

    private rejectPendingConditionalFormatRulesRequests(error: Error) {
        for (const pending of this.pendingConditionalFormatRulesRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingConditionalFormatRulesRequests.clear();
    }

    private rejectPendingSparklineGroupsRequests(error: Error) {
        for (const pending of this.pendingSparklineGroupsRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingSparklineGroupsRequests.clear();
    }

    private rejectPendingDerivedArtifactsRequests(error: Error) {
        for (const pending of this.pendingDerivedArtifactsRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingDerivedArtifactsRequests.clear();
    }

    private rejectPendingValidationDropdownOptionsRequests(error: Error) {
        for (const pending of this.pendingValidationDropdownOptionsRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingValidationDropdownOptionsRequests.clear();
    }

    private rejectPendingValidationIssuesRequests(error: Error) {
        for (const pending of this.pendingValidationIssuesRequests.values()) {
            try { pending.reject(error); } catch { }
        }
        this.pendingValidationIssuesRequests.clear();
    }

    private sendHistoryCommand(commandName: 'sheet.undo' | 'sheet.redo') {
        const requestId = Math.random().toString(36).substring(7);
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const command = buildSpreadsheetWorkerCommand<Record<string, never>>({
            command: commandName,
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {},
        });
        this.bus.sendCommand(command.command, command);
    }

    private engineSheetIdForUiSheet(uiSheetId?: number | null): string {
        return buildSpreadsheetEngineSheetId(uiSheetId ?? this.pinnedSheetId ?? this.activeSheetId);
    }

    private sendStructuralOperation(
        opType: SpreadsheetStructuralOperationType,
        payload: SpreadsheetStructuralOperationPayload,
        opts?: ProjectionWriteOptions,
    ): SpreadsheetEngineOperationEnvelope {
        const publishAcceptedOperations = opts?.publishAcceptedOperations !== false;
        const requestId = Math.random().toString(36).substring(7);
        const sheetId = payload.sheet_id;
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_${opType}_${Date.now()}_${requestId}`,
            op_type: opType,
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload,
        });
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: [operation],
                allowQueryTabEdit: this.options.allowQueryTabEdits,
                ...(publishAcceptedOperations ? { returnAcceptedOperations: true } : null),
            },
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations,
            undo: {
                label: opType,
                operationIds: [operation.op_id],
                skipUndoRegistration: opts?.skipUndoRegistration,
            },
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return operation;
    }

    private handleDataChunk(payload: SpreadsheetViewportResponsePayload) {
        const chunk = payload.data;
        const sheetId = Number(payload.sheet_id);
        const visibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        if (
            Number.isFinite(sheetId) && sheetId > 0
            && Number.isFinite(Number(visibleSheetId)) && Number(visibleSheetId) > 0
            && sheetId !== Number(visibleSheetId)
        ) {
            return;
        }

        perf('streamer.handleDataChunk', () => {
            // Keep cache bounded to the current viewport to avoid mobile GC/memory blowups.
            this.cellCache.clear();
            for (const item of chunk) {
                const col = Math.floor(Number(item.col));
                const row = Math.floor(Number(item.row));
                if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
                const data = item.data;
                const next: CellData = { value: String(data.value ?? '') };
                if (typeof data.formula === 'string') next.formula = data.formula;
                if (isRecord(data.format)) next.format = data.format as CellFormat;
                if (typeof data.validation_id === 'string' && data.validation_id) next.validationId = data.validation_id;
                if (typeof data.comment === 'string' && data.comment) next.comment = data.comment;
                if (typeof data.hyperlink === 'string' && data.hyperlink) next.hyperlink = data.hyperlink;
                if (isRecord(data.protection)) next.protection = data.protection;
                if (isRecord(data.metadata)) next.metadata = data.metadata;
                const key = `${col},${row}`;
                this.cellCache.set(key, next);
            }
            this.notifyProjectionChanged();
        });
    }

    private invalidateVisibleSheetState() {
        const vp = this.lastViewport;
        this.cellCache.clear();
        this.lastViewport = null;
        this.notifyProjectionChanged();
        if (vp) {
            this.updateViewport(vp.x, vp.y, vp.width, vp.height);
        } else if (this.viewportUpdater) {
            this.viewportUpdater();
        }
    }

    private registerStyleSidecars(styles: unknown) {
        const normalized = normalizeSpreadsheetStyleSidecars(styles);
        if (!normalized) return;
        for (const [styleId, style] of Object.entries(normalized)) {
            this.styleSidecars.set(styleId, { ...(style as CellFormat) });
        }
    }

    private currentSheetEntry(): SpreadsheetWorkerSheetListEntry | null {
        const visibleSheetId = this.pinnedSheetId ?? this.activeSheetId;
        const numericSheetId = Math.floor(Number(visibleSheetId));
        if (!Number.isFinite(numericSheetId) || numericSheetId <= 0) return null;
        return this.knownSheets.find((sheet) => Math.floor(Number(sheet.id)) === numericSheetId) ?? null;
    }

    private styleFormatFromIndexedMetadata(
        metadata: Record<string, Record<string, unknown>> | undefined,
        index: number,
    ): CellFormat | null {
        if (!metadata) return null;
        const normalizedIndex = Math.max(0, Math.floor(Number(index) || 0));
        const entry = metadata[String(normalizedIndex)];
        if (!isRecord(entry)) return null;
        const styleId = String(entry.style_id ?? '').trim();
        if (!styleId) return null;
        const style = this.styleSidecars.get(styleId);
        return style ? { ...style } : null;
    }

    getRowFormat(rowIndex: number): CellFormat | null {
        return this.styleFormatFromIndexedMetadata(this.currentSheetEntry()?.row_metadata, rowIndex);
    }

    getColumnFormat(colIndex: number): CellFormat | null {
        return this.styleFormatFromIndexedMetadata(this.currentSheetEntry()?.column_metadata, colIndex);
    }

    private normalizeSheetList(payload: SpreadsheetSheetListEntry[] | null | undefined): SpreadsheetWorkerSheetListEntry[] {
        if (!Array.isArray(payload)) return this.knownSheets;
        const sheets = payload.map((sheet): SpreadsheetWorkerSheetListEntry | null => {
            const id = Math.floor(Number(sheet?.id));
            if (!Number.isFinite(id) || id <= 0) return null;
            const normalizedRows = Math.floor(Number(sheet?.row_count));
            const normalizedCols = Math.floor(Number(sheet?.col_count));
            const rowMetadata = sheet?.row_metadata && typeof sheet.row_metadata === 'object'
                ? sheet.row_metadata
                : undefined;
            const columnMetadata = sheet?.column_metadata && typeof sheet.column_metadata === 'object'
                ? sheet.column_metadata
                : undefined;
            const frozenPanes = sheet?.frozen_panes && typeof sheet.frozen_panes === 'object'
                ? {
                    rows: Math.max(0, Math.floor(Number(sheet.frozen_panes.rows) || 0)),
                    cols: Math.max(0, Math.floor(Number(sheet.frozen_panes.cols) || 0)),
                }
                : undefined;
            const sheetProtection = sheet?.sheet_protection && typeof sheet.sheet_protection === 'object'
                ? sheet.sheet_protection
                : undefined;
            const autoFilter = sheet?.auto_filter && typeof sheet.auto_filter === 'object'
                ? sheet.auto_filter
                : undefined;
            const tables = sheet?.tables && typeof sheet.tables === 'object'
                ? sheet.tables
                : undefined;
            return {
                id,
                name: String(sheet?.name ?? `Sheet${id}`),
                ...(Number.isFinite(normalizedRows) && normalizedRows > 0 ? { row_count: normalizedRows } : null),
                ...(Number.isFinite(normalizedCols) && normalizedCols > 0 ? { col_count: normalizedCols } : null),
                ...(rowMetadata ? { row_metadata: rowMetadata } : null),
                ...(columnMetadata ? { column_metadata: columnMetadata } : null),
                ...(frozenPanes ? { frozen_panes: frozenPanes } : null),
                ...(sheet?.hidden === true ? { hidden: true } : null),
                ...(sheetProtection ? { sheet_protection: sheetProtection } : null),
                ...(autoFilter ? { auto_filter: autoFilter } : null),
                ...(tables ? { tables } : null),
            };
        }).filter((sheet): sheet is SpreadsheetWorkerSheetListEntry => sheet !== null);
        return sheets.length > 0 ? sheets : this.knownSheets;
    }

    private nextSheetNumber(): number {
        const used = new Set(this.knownSheets.map((sheet) => sheet.id).filter((id) => Number.isFinite(id) && id > 0));
        let candidate = 1;
        while (used.has(candidate)) candidate += 1;
        return candidate;
    }

    getCellValue(col: number, row: number): string | undefined {
        const key = `${col},${row}`;
        return this.cellCache.get(key)?.value;
    }

    getCellFormula(col: number, row: number): string | undefined {
        const key = `${col},${row}`;
        return this.cellCache.get(key)?.formula;
    }

    getCellFormat(col: number, row: number): CellFormat | undefined {
        const key = `${col},${row}`;
        return this.cellCache.get(key)?.format;
    }

    getCellMetadata(col: number, row: number): Pick<CellData, 'validationId' | 'comment' | 'hyperlink' | 'protection' | 'metadata'> | undefined {
        const key = `${col},${row}`;
        const cell = this.cellCache.get(key);
        if (!cell) return undefined;
        const result: Pick<CellData, 'validationId' | 'comment' | 'hyperlink' | 'protection' | 'metadata'> = {};
        if (cell.validationId) result.validationId = cell.validationId;
        if (cell.comment) result.comment = cell.comment;
        if (cell.hyperlink) result.hyperlink = cell.hyperlink;
        if (cell.protection) result.protection = cell.protection;
        if (cell.metadata) result.metadata = cell.metadata;
        return Object.keys(result).length > 0 ? result : undefined;
    }

    private sendSetCellInput(
        col: number,
        row: number,
        value: string,
        uiSheetId?: number | null,
        opts?: {
            returnAcceptedOperations?: boolean;
            validationOverride?: boolean;
        } & PendingUndoRegistrationOptions,
    ): { requestId: string; operation: SpreadsheetEngineOperationEnvelope } {
        const sheetId = buildSpreadsheetEngineSheetId(uiSheetId ?? this.pinnedSheetId ?? this.activeSheetId);
        const rawInput = String(value ?? '');
        const transformedInput = this.options.transformCellInput
            ? this.options.transformCellInput({ sheetId, col, row, rawInput })
            : rawInput;
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_set_cell_${Date.now()}_${requestId}`,
            op_type: 'SetCellInput',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                address: { sheet_id: sheetId, row, col },
                raw_input: transformedInput,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
            },
        });
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: [operation],
                allowQueryTabEdit: this.options.allowQueryTabEdits,
                ...(opts?.returnAcceptedOperations ? { returnAcceptedOperations: true } : {}),
            },
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: Boolean(opts?.returnAcceptedOperations),
            undo: opts?.label
                ? { label: opts.label, operationIds: [operation.op_id], skipUndoRegistration: opts.skipUndoRegistration }
                : undefined,
        });
        return { requestId, operation };
    }

    private sendSetFormatRange(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        format: CellFormat,
        uiSheetId?: number | null,
        opts?: { returnAcceptedOperations?: boolean } & PendingUndoRegistrationOptions,
    ): { requestId: string; operation: SpreadsheetEngineOperationEnvelope } {
        const sheetId = buildSpreadsheetEngineSheetId(uiSheetId ?? this.pinnedSheetId ?? this.activeSheetId);
        const { styleId, style } = buildSpreadsheetStyleFromFormat(format as Record<string, unknown>);
        const requestId = Math.random().toString(36).substring(7);
        const normalizedStartCol = Math.min(startCol, endCol);
        const normalizedStartRow = Math.min(startRow, endRow);
        const normalizedEndCol = Math.max(startCol, endCol);
        const normalizedEndRow = Math.max(startRow, endRow);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_set_format_${Date.now()}_${requestId}`,
            op_type: 'SetFormat',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: normalizedStartRow,
                    start_col: normalizedStartCol,
                    end_row: normalizedEndRow,
                    end_col: normalizedEndCol,
                },
                style_id: styleId,
            },
        });
        const payload: SpreadsheetApplyOpsPayload = {
            ops: [operation],
            allowQueryTabEdit: this.options.allowQueryTabEdits,
        };
        if (opts?.returnAcceptedOperations) payload.returnAcceptedOperations = true;
        if (styleId && style) payload.styles = { [styleId]: style };
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: Boolean(opts?.returnAcceptedOperations),
            undo: opts?.label
                ? { label: opts.label, operationIds: [operation.op_id], skipUndoRegistration: opts.skipUndoRegistration }
                : undefined,
        });
        return { requestId, operation };
    }

    private sendSpreadsheetEngineOperations(
        operations: SpreadsheetEngineOperationEnvelope[],
        uiSheetId?: number | null,
        opts?: PendingUndoRegistrationOptions,
    ): { requestId: string; operations: SpreadsheetEngineOperationEnvelope[] } | null {
        if (!Array.isArray(operations) || operations.length <= 0) return null;
        const sheetId = buildSpreadsheetEngineSheetId(uiSheetId ?? this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: operations,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
                returnAcceptedOperations: true,
            },
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: opts?.label
                ? {
                    label: opts.label,
                    operationIds: operations.map((operation) => operation.op_id),
                    skipUndoRegistration: opts.skipUndoRegistration,
                }
                : undefined,
        });
        return { requestId, operations };
    }

    private setCellValueInternal(col: number, row: number, value: string, opts?: { validationOverride?: boolean }) {
        // Optimistic update
        const key = `${col},${row}`;
        const previous = this.cellCache.get(key);
        const current = previous || { value: '' };

        const isFormula = value.trimStart().startsWith('=');
        if (isFormula) {
            // Keep displaying the previous computed value until worker recalculates,
            // but store the formula text so the formula bar can show it.
            this.cellCache.set(key, { ...current, formula: value });
        } else {
            this.cellCache.set(key, { ...current, value, formula: undefined });
        }

        const { requestId } = this.sendSetCellInput(col, row, value, undefined, {
            returnAcceptedOperations: true,
            label: 'Edit cell',
            validationOverride: opts?.validationOverride === true,
        });
        this.pendingOptimisticCellEdits.set(requestId, {
            col,
            row,
            value,
            previous: previous ? { ...previous } : undefined,
        });

        // Force a viewport refresh so computed values (including formulas) come back promptly.
        // `updateViewport` will coalesce to at most once per frame.
        if (this.lastViewport) {
            const vp = this.lastViewport;
            // Bust the "same viewport" guard.
            this.lastViewport = null;
            this.updateViewport(vp.x, vp.y, vp.width, vp.height);
        }
    }

    setCellValue(col: number, row: number, value: string, opts?: { validationOverride?: boolean }) {
        this.setCellValueInternal(col, row, value, opts);
    }

    defineName(
        name: string,
        range: SpreadsheetRangeAddress,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetDefineNamePayload> {
        const trimmedName = String(name ?? '').trim();
        if (!trimmedName) throw new Error('Defined name is required');
        const sheetId = String(range?.sheet_id || this.engineSheetIdForUiSheet(opts?.sheetId)).trim();
        const normalizedRange = normalizeSpreadsheetRange({ ...range, sheet_id: sheetId });
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetDefineNamePayload>({
            op_id: `viewport_streamer_DefineName_${Date.now()}_${requestId}`,
            op_type: 'DefineName',
            workbook_id: 'active_workbook',
            sheet_id: normalizedRange.sheet_id,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                name: trimmedName,
                range: normalizedRange,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: 'Define name',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        return operation;
    }

    upsertDefinedName(
        input: {
            name: string;
            scope?: SpreadsheetDefinedNameScope;
            refersTo?: string | null;
            range?: SpreadsheetRangeAddress | null;
            comment?: string | null;
            hidden?: boolean;
        },
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetDefineNamePayload> {
        const trimmedName = String(input?.name ?? '').trim();
        if (!trimmedName) throw new Error('Defined name is required');
        const scope: SpreadsheetDefinedNameScope = input?.scope?.kind === 'sheet'
            ? {
                kind: 'sheet',
                sheet_id: String(input.scope.sheet_id || this.engineSheetIdForUiSheet(opts?.sheetId)).trim(),
            }
            : { kind: 'workbook' };
        const range = input?.range
            ? normalizeSpreadsheetRange({
                ...input.range,
                sheet_id: String(input.range.sheet_id || (scope.kind === 'sheet' ? scope.sheet_id : this.engineSheetIdForUiSheet(opts?.sheetId))).trim(),
            })
            : null;
        const rawRefersTo = String(input?.refersTo ?? '').trim();
        const refersTo = range
            ? (rawRefersTo || undefined)
            : rawRefersTo
                ? (rawRefersTo.startsWith('=') ? rawRefersTo : `=${rawRefersTo}`)
                : '';
        if (!range && !refersTo) throw new Error('Defined name reference is required');
        const sheetId = scope.kind === 'sheet'
            ? scope.sheet_id
            : range?.sheet_id ?? this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetDefineNamePayload>({
            op_id: `viewport_streamer_DefineName_${Date.now()}_${requestId}`,
            op_type: 'DefineName',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                name: trimmedName,
                scope,
                ...(range ? { range } : null),
                ...(refersTo ? { refers_to: refersTo } : null),
                ...(input?.comment == null ? null : { comment: String(input.comment) }),
                ...(input?.hidden === true ? { hidden: true } : null),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: 'Define name',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        return operation;
    }

    deleteName(
        name: string,
        opts?: { sheetId?: number | null; scope?: SpreadsheetDefinedNameScope; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetDeleteNamePayload> {
        const trimmedName = String(name ?? '').trim();
        if (!trimmedName) throw new Error('Defined name is required');
        const scope = opts?.scope?.kind === 'sheet'
            ? {
                kind: 'sheet' as const,
                sheet_id: String(opts.scope.sheet_id || this.engineSheetIdForUiSheet(opts?.sheetId)).trim(),
            }
            : opts?.scope?.kind === 'workbook'
                ? { kind: 'workbook' as const }
                : undefined;
        const sheetId = scope?.kind === 'sheet' ? scope.sheet_id : this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetDeleteNamePayload>({
            op_id: `viewport_streamer_DeleteName_${Date.now()}_${requestId}`,
            op_type: 'DeleteName',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                name: trimmedName,
                ...(scope ? { scope } : null),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: 'Delete name',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        return operation;
    }

    setCellComment(
        col: number,
        row: number,
        comment: string | null,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetCommentPayload> {
        const targetCol = Math.max(0, Math.floor(Number(col) || 0));
        const targetRow = Math.max(0, Math.floor(Number(row) || 0));
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const trimmedComment = comment === null ? null : String(comment ?? '').trim();
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetCommentPayload>({
            op_id: `viewport_streamer_SetComment_${Date.now()}_${requestId}`,
            op_type: 'SetComment',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                address: { sheet_id: sheetId, row: targetRow, col: targetCol },
                comment: trimmedComment || null,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: operation.payload.comment ? 'Set comment' : 'Clear comment',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return operation;
    }

    setRangeValidation(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        validationId: string | null,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetValidationPayload> {
        const normalizedStartCol = Math.max(0, Math.min(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedEndCol = Math.max(0, Math.max(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedStartRow = Math.max(0, Math.min(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const normalizedEndRow = Math.max(0, Math.max(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const trimmedValidationId = validationId === null ? null : String(validationId ?? '').trim();
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetValidationPayload>({
            op_id: `viewport_streamer_SetValidation_${Date.now()}_${requestId}`,
            op_type: 'SetValidation',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: normalizedStartRow,
                    start_col: normalizedStartCol,
                    end_row: normalizedEndRow,
                    end_col: normalizedEndCol,
                },
                validation_id: trimmedValidationId || null,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: operation.payload.validation_id ? 'Set data validation' : 'Clear data validation',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return operation;
    }

    setRangeValidationRule(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        rule: Omit<SpreadsheetValidationRule, 'id'> & { id?: string | null },
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): {
        upsertOperation: SpreadsheetEngineOperationEnvelope<SpreadsheetUpsertValidationRulePayload>;
        setValidationOperation: SpreadsheetEngineOperationEnvelope<SpreadsheetSetValidationPayload>;
        rule: SpreadsheetValidationRule;
    } {
        const normalizedStartCol = Math.max(0, Math.min(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedEndCol = Math.max(0, Math.max(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedStartRow = Math.max(0, Math.min(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const normalizedEndRow = Math.max(0, Math.max(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const normalizedRule = canonicalizeSpreadsheetValidationRule(rule);
        const upsertOperation = buildSpreadsheetOperationEnvelope<SpreadsheetUpsertValidationRulePayload>({
            op_id: `viewport_streamer_UpsertValidationRule_${Date.now()}_${requestId}`,
            op_type: 'UpsertValidationRule',
            workbook_id: 'active_workbook',
            sheet_id: null,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { rule: normalizedRule },
        });
        const setValidationOperation = buildSpreadsheetOperationEnvelope<SpreadsheetSetValidationPayload>({
            op_id: `viewport_streamer_SetValidation_${Date.now()}_${requestId}`,
            op_type: 'SetValidation',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: normalizedStartRow,
                    start_col: normalizedStartCol,
                    end_row: normalizedEndRow,
                    end_col: normalizedEndCol,
                },
                validation_id: normalizedRule.id,
            },
        });
        this.sendSpreadsheetEngineOperations([upsertOperation, setValidationOperation], opts?.sheetId, {
            label: 'Set data validation',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return { upsertOperation, setValidationOperation, rule: normalizedRule };
    }

    setCellHyperlink(
        col: number,
        row: number,
        hyperlink: string | null,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetHyperlinkPayload> {
        const targetCol = Math.max(0, Math.floor(Number(col) || 0));
        const targetRow = Math.max(0, Math.floor(Number(row) || 0));
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const trimmedHyperlink = hyperlink === null ? null : String(hyperlink ?? '').trim();
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetHyperlinkPayload>({
            op_id: `viewport_streamer_SetHyperlink_${Date.now()}_${requestId}`,
            op_type: 'SetHyperlink',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                address: { sheet_id: sheetId, row: targetRow, col: targetCol },
                hyperlink: trimmedHyperlink || null,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: operation.payload.hyperlink ? 'Set hyperlink' : 'Clear hyperlink',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return operation;
    }

    setRangeProtection(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        protection: Record<string, unknown> | null,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetProtectionPayload> {
        const normalizedStartCol = Math.max(0, Math.min(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedEndCol = Math.max(0, Math.max(
            Math.floor(Number(startCol) || 0),
            Math.floor(Number(endCol) || 0),
        ));
        const normalizedStartRow = Math.max(0, Math.min(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const normalizedEndRow = Math.max(0, Math.max(
            Math.floor(Number(startRow) || 0),
            Math.floor(Number(endRow) || 0),
        ));
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetProtectionPayload>({
            op_id: `viewport_streamer_SetProtection_${Date.now()}_${requestId}`,
            op_type: 'SetProtection',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: normalizedStartRow,
                    start_col: normalizedStartCol,
                    end_row: normalizedEndRow,
                    end_col: normalizedEndCol,
                },
                protection,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: operation.payload.protection ? 'Protect cells' : 'Clear protection',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        this.notifyProjectionChanged();
        return operation;
    }

    setSheetProtection(
        protection: Record<string, unknown> | null,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ): SpreadsheetEngineOperationEnvelope<SpreadsheetSetSheetProtectionPayload> {
        const sheetId = this.engineSheetIdForUiSheet(opts?.sheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope<SpreadsheetSetSheetProtectionPayload>({
            op_id: `viewport_streamer_SetSheetProtection_${Date.now()}_${requestId}`,
            op_type: 'SetSheetProtection',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                protection,
            },
        });
        this.sendSpreadsheetEngineOperations([operation], opts?.sheetId, {
            label: operation.payload.protection ? 'Protect sheet' : 'Unprotect sheet',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        this.cellCache.clear();
        void this.getSheets();
        this.notifyProjectionChanged();
        return operation;
    }

    private setCellFormatInternal(col: number, row: number, format: CellFormat) {
        const key = `${col},${row}`;
        const current = this.cellCache.get(key) || { value: '' };
        const mergedFormat = mergeCellFormat(current.format, format);
        this.cellCache.set(key, { ...current, format: mergedFormat });

        this.sendSetFormatRange(col, row, col, row, mergedFormat, undefined, {
            returnAcceptedOperations: true,
            label: 'Format cell',
        });
    }

    setCellFormat(col: number, row: number, format: CellFormat) {
        this.setCellFormatInternal(col, row, format);
    }

    setRangeFormat(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        format: CellFormat,
        opts?: ProjectionWriteOptions,
    ) {
        const normalizedStartCol = Math.min(startCol, endCol);
        const normalizedStartRow = Math.min(startRow, endRow);
        const normalizedEndCol = Math.max(startCol, endCol);
        const normalizedEndRow = Math.max(startRow, endRow);
        for (const [key, current] of this.cellCache.entries()) {
            const [rawCol, rawRow] = key.split(',');
            const col = Number(rawCol);
            const row = Number(rawRow);
            if (
                Number.isFinite(col)
                && Number.isFinite(row)
                && col >= normalizedStartCol
                && col <= normalizedEndCol
                && row >= normalizedStartRow
                && row <= normalizedEndRow
            ) {
                this.cellCache.set(key, { ...current, format: mergeCellFormat(current.format, format) });
            }
        }

        this.sendSetFormatRange(normalizedStartCol, normalizedStartRow, normalizedEndCol, normalizedEndRow, format, opts?.sheetId, {
            returnAcceptedOperations: opts?.publishAcceptedOperations !== false,
            label: 'Format cells',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
    }

    clearRangeFormats(startCol: number, startRow: number, endCol: number, endRow: number) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const normalizedStartCol = Math.min(startCol, endCol);
        const normalizedStartRow = Math.min(startRow, endRow);
        const normalizedEndCol = Math.max(startCol, endCol);
        const normalizedEndRow = Math.max(startRow, endRow);
        for (const [key, current] of this.cellCache.entries()) {
            const [rawCol, rawRow] = key.split(',');
            const col = Number(rawCol);
            const row = Number(rawRow);
            if (
                Number.isFinite(col)
                && Number.isFinite(row)
                && col >= normalizedStartCol
                && col <= normalizedEndCol
                && row >= normalizedStartRow
                && row <= normalizedEndRow
            ) {
                this.cellCache.set(key, { ...current, format: {} });
            }
        }
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_clear_formats_${Date.now()}_${requestId}`,
            op_type: 'ClearFormats',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: normalizedStartRow,
                    start_col: normalizedStartCol,
                    end_row: normalizedEndRow,
                    end_col: normalizedEndCol,
                },
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Clear formats' });
    }

    setRowFormat(rowIndex: number, count: number, format: CellFormat | null) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const { styleId, style } = buildSpreadsheetStyleFromFormat(format as Record<string, unknown> | null);
        if (styleId && style) this.registerStyleSidecars({ [styleId]: style });
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_set_row_format_${Date.now()}_${requestId}`,
            op_type: 'SetRowFormat',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                row_index: Math.max(0, Math.floor(Number(rowIndex) || 0)),
                count: Math.max(1, Math.floor(Number(count) || 1)),
                style_id: styleId,
            },
        });
        const payload: SpreadsheetApplyOpsPayload = {
            ops: [operation],
            allowQueryTabEdit: this.options.allowQueryTabEdits,
            returnAcceptedOperations: true,
        };
        if (styleId && style) payload.styles = { [styleId]: style };
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: { label: 'Format rows', operationIds: [operation.op_id] },
        });
    }

    setColumnFormat(colIndex: number, count: number, format: CellFormat | null) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const { styleId, style } = buildSpreadsheetStyleFromFormat(format as Record<string, unknown> | null);
        if (styleId && style) this.registerStyleSidecars({ [styleId]: style });
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_set_column_format_${Date.now()}_${requestId}`,
            op_type: 'SetColumnFormat',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                col_index: Math.max(0, Math.floor(Number(colIndex) || 0)),
                count: Math.max(1, Math.floor(Number(count) || 1)),
                style_id: styleId,
            },
        });
        const payload: SpreadsheetApplyOpsPayload = {
            ops: [operation],
            allowQueryTabEdit: this.options.allowQueryTabEdits,
            returnAcceptedOperations: true,
        };
        if (styleId && style) payload.styles = { [styleId]: style };
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: { label: 'Format columns', operationIds: [operation.op_id] },
        });
    }

    mergeRange(startCol: number, startRow: number, endCol: number, endRow: number) {
        this.sendMergeOperation('MergeCells', startCol, startRow, endCol, endRow, 'Merge cells');
    }

    mergeAcrossRange(startCol: number, startRow: number, endCol: number, endRow: number) {
        this.sendMergeOperation('MergeAcross', startCol, startRow, endCol, endRow, 'Merge across');
    }

    unmergeRange(startCol: number, startRow: number, endCol: number, endRow: number) {
        this.sendMergeOperation('UnmergeCells', startCol, startRow, endCol, endRow, 'Unmerge cells');
    }

    upsertConditionalFormatRule(rule: SpreadsheetConditionalFormatRule) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_upsert_conditional_format_${Date.now()}_${requestId}`,
            op_type: 'UpsertConditionalFormatRule',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { rule },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Conditional formatting' });
    }

    deleteConditionalFormatRule(ruleId: string) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_delete_conditional_format_${Date.now()}_${requestId}`,
            op_type: 'DeleteConditionalFormatRule',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { rule_id: String(ruleId || '').trim() },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Delete conditional formatting rule' });
    }

    reorderConditionalFormatRules(ruleIds: string[]) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_reorder_conditional_formats_${Date.now()}_${requestId}`,
            op_type: 'ReorderConditionalFormatRules',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                rule_ids: ruleIds.map((ruleId) => String(ruleId || '').trim()).filter(Boolean),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Reorder conditional formats' });
    }

    clearConditionalFormats(range?: SpreadsheetRangeAddress | null) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_clear_conditional_formats_${Date.now()}_${requestId}`,
            op_type: 'ClearConditionalFormats',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                ...(range ? { range } : null),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Clear conditional formats' });
    }

    upsertSparklineGroup(group: SpreadsheetSparklineGroup) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_upsert_sparkline_${Date.now()}_${requestId}`,
            op_type: 'UpsertSparklineGroup',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { group },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Sparkline' });
    }

    deleteSparklineGroup(groupId: string) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_delete_sparkline_${Date.now()}_${requestId}`,
            op_type: 'DeleteSparklineGroup',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { group_id: String(groupId || '').trim() },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Delete sparkline group' });
    }

    clearSparklines(range?: SpreadsheetRangeAddress | null) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_clear_sparklines_${Date.now()}_${requestId}`,
            op_type: 'ClearSparklines',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                sheet_id: sheetId,
                ...(range ? { range } : null),
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Clear sparklines' });
    }

    groupSparklineLocations(groupId: string, locations?: SpreadsheetRangeAddress[], newGroupId?: string | null) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_group_sparklines_${Date.now()}_${requestId}`,
            op_type: 'GroupSparklineLocations',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                group_id: String(groupId || '').trim(),
                ...(newGroupId ? { new_group_id: String(newGroupId).trim() } : null),
                locations: Array.isArray(locations) ? locations : [],
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Group sparklines' });
    }

    ungroupSparklineGroup(groupId: string) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_ungroup_sparklines_${Date.now()}_${requestId}`,
            op_type: 'UngroupSparklineGroup',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: { group_id: String(groupId || '').trim() },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label: 'Ungroup sparklines' });
    }

    private sendMergeOperation(
        opType: 'MergeCells' | 'MergeAcross' | 'UnmergeCells',
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        label: string,
    ) {
        const sheetId = buildSpreadsheetEngineSheetId(this.pinnedSheetId ?? this.activeSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_${opType}_${Date.now()}_${requestId}`,
            op_type: opType,
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: Math.min(startRow, endRow),
                    start_col: Math.min(startCol, endCol),
                    end_row: Math.max(startRow, endRow),
                    end_col: Math.max(startCol, endCol),
                },
            },
        });
        this.sendSpreadsheetEngineOperations([operation], undefined, { label });
    }

    applyRemoteSpreadsheetEngineOperation(
        operation: SpreadsheetEngineOperationEnvelope,
        styles?: Record<string, Record<string, unknown>>,
    ) {
        const sheetId = String(operation?.sheet_id ?? '').trim() || null;
        const requestId = Math.random().toString(36).substring(7);
        const replayStyles = normalizeSpreadsheetStyleSidecars(styles)
            ?? getSpreadsheetOperationStyleSidecars(operation)
            ?? undefined;
        const payload: SpreadsheetApplyOpsPayload = {
            ops: [operation],
            allowQueryTabEdit: this.options.allowQueryTabEdits,
        };
        if (replayStyles && Object.keys(replayStyles).length > 0) payload.styles = replayStyles;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload,
        });
        this.sendCommandWithPendingUndo(command, { publishAcceptedOperations: false });
        const opType = String(operation?.op_type ?? '').trim();
        if (
            opType === 'SetFrozenPanes'
            || opType === 'SetHiddenRows'
            || opType === 'SetHiddenColumns'
            || opType === 'SetRowFormat'
            || opType === 'SetColumnFormat'
            || opType === 'MergeCells'
            || opType === 'UnmergeCells'
            || opType === 'SetAutoFilter'
            || opType === 'ClearAutoFilter'
            || opType === 'UpsertTable'
            || opType === 'DeleteTable'
        ) {
            this.pendingSheetMetadataRefreshRequestIds.add(requestId);
        }
    }

    private lastViewport: { x: number, y: number, width: number, height: number } | null = null;

    updateViewport(x: number, y: number, width: number, height: number) {
        // Coalesce frequent scroll updates to at most one per frame (critical on mobile).
        this.queuedViewport = { x, y, width, height };
        if (this.viewportRafId !== null) return;

        this.viewportRafId = requestAnimationFrame(() => {
            this.viewportRafId = null;
            const vp = this.queuedViewport;
            this.queuedViewport = null;
            if (!vp) return;

            // Prevent infinite loop of Render -> Update -> Data -> Render
            if (this.lastViewport &&
                this.lastViewport.x === vp.x &&
                this.lastViewport.y === vp.y &&
                this.lastViewport.width === vp.width &&
                this.lastViewport.height === vp.height) {
                return;
            }

            this.lastViewport = vp;

            const seq = ++this.viewportSeq;
            this.latestViewportSeqSent = seq;

            perf('streamer.updateViewport', () => {
                const payload: SpreadsheetGetViewportPayload = { x: vp.x, y: vp.y, width: vp.width, height: vp.height, seq };
                if (this.options.forceSpreadsheetViewport) payload.forceSpreadsheet = true;
                payload.gridCols = this.gridDimensions.cols;
                payload.gridRows = this.gridDimensions.rows;
                const viewportSheetId = this.pinnedSheetId ?? this.activeSheetId;
                const viewportUiSheetId = Math.floor(Number(viewportSheetId));
                const frozenPanes = Number.isFinite(viewportUiSheetId) && viewportUiSheetId > 0
                    ? (this.frozenPanesBySheetId.get(viewportUiSheetId) ?? this.knownSheets.find((sheet) => Math.floor(Number(sheet.id)) === viewportUiSheetId)?.frozen_panes ?? null)
                    : null;
                if (frozenPanes) {
                    payload.frozenRows = Math.max(0, Math.floor(Number(frozenPanes.rows) || 0));
                    payload.frozenCols = Math.max(0, Math.floor(Number(frozenPanes.cols) || 0));
                }
                if (Number.isFinite(Number(viewportSheetId)) && Number(viewportSheetId) > 0) {
                    payload.sheetId = Math.floor(Number(viewportSheetId));
                }
                const sheetId = buildSpreadsheetEngineSheetId(viewportSheetId);
                const command = buildSpreadsheetWorkerCommand<SpreadsheetGetViewportPayload>({
                    command: 'sheet.getViewport',
                    requestId: `viewport_${seq}`,
                    workbookId: 'active_workbook',
                    sheetId,
                    baseRevision: 0,
                    clientId: 'viewport_streamer',
                    payload,
                });
                this.bus.sendCommand(command.command, command);
            });
        });
    }

    async getRangeData(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        options?: { sheetId?: string | number | null },
    ): Promise<string[][]> {
        return new Promise((resolve, reject) => {
            const requestId = Math.random().toString(36).substring(7);
            this.pendingRequests.set(requestId, { resolve, reject });
            const rangeSheetId = options?.sheetId ?? this.pinnedSheetId ?? this.activeSheetId;
            const sheetId = buildSpreadsheetEngineSheetId(rangeSheetId);
            const command = buildSpreadsheetWorkerCommand<SpreadsheetGetRangePayload>({
                command: 'sheet.getRange',
                requestId,
                workbookId: 'active_workbook',
                sheetId,
                baseRevision: 0,
                clientId: 'viewport_streamer',
                payload: {
                    range: {
                        sheet_id: sheetId,
                        start_row: startRow,
                        start_col: startCol,
                        end_row: endRow,
                        end_col: endCol,
                    },
                    mode: 'raw',
                },
            });
            this.bus.sendCommand(command.command, command);
        });
    }

    async getRangeCells(
        startCol: number,
        startRow: number,
        endCol: number,
        endRow: number,
        mode: 'raw' | 'computed' = 'computed',
        options?: { sheetId?: string | number | null },
    ): Promise<SpreadsheetRangeCell[][]> {
        return new Promise((resolve, reject) => {
            const requestId = Math.random().toString(36).substring(7);
            this.pendingRangeCellsRequests.set(requestId, { resolve, reject });
            const rangeSheetId = options?.sheetId ?? this.pinnedSheetId ?? this.activeSheetId;
            const sheetId = buildSpreadsheetEngineSheetId(rangeSheetId);
            const command = buildSpreadsheetWorkerCommand<SpreadsheetGetRangePayload>({
                command: 'sheet.getRange',
                requestId,
                workbookId: 'active_workbook',
                sheetId,
                baseRevision: 0,
                clientId: 'viewport_streamer',
                payload: {
                    range: {
                        sheet_id: sheetId,
                        start_row: startRow,
                        start_col: startCol,
                        end_row: endRow,
                        end_col: endCol,
                    },
                    mode,
                },
            });
            this.bus.sendCommand(command.command, command);
        });
    }

    async copyRangeTsv(startCol: number, startRow: number, endCol: number, endRow: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const requestId = Math.random().toString(36).substring(7);
            this.pendingCopyRequests.set(requestId, { resolve, reject });
            const rangeSheetId = this.pinnedSheetId ?? this.activeSheetId;
            const sheetId = buildSpreadsheetEngineSheetId(rangeSheetId);
            const command = buildSpreadsheetWorkerCommand<SpreadsheetCopyRangePayload>({
                command: 'sheet.copyRange',
                requestId,
                workbookId: 'active_workbook',
                sheetId,
                baseRevision: 0,
                clientId: 'viewport_streamer',
                payload: {
                    range: {
                        sheet_id: sheetId,
                        start_row: startRow,
                        start_col: startCol,
                        end_row: endRow,
                        end_col: endCol,
                    },
                },
            });
            this.bus.sendCommand(command.command, command);
        });
    }

    hasEngineClipboard(): boolean {
        return this.lastEngineClipboard !== null;
    }

    getEngineClipboardPasteShape(mode: SpreadsheetPasteSpecialMode): { rowCount: number; colCount: number } | null {
        const clipboard = this.lastEngineClipboard;
        if (!clipboard) return null;
        const rowCount = Math.max(0, Math.floor(Number(clipboard.row_count) || 0));
        const colCount = Math.max(0, Math.floor(Number(clipboard.col_count) || 0));
        if (rowCount <= 0 || colCount <= 0) return null;
        if (mode === 'transpose') {
            return { rowCount: colCount, colCount: rowCount };
        }
        return { rowCount, colCount };
    }

    pasteSpecialFromLastCopy(startCol: number, startRow: number, mode: SpreadsheetPasteSpecialMode): boolean {
        const clipboard = this.lastEngineClipboard;
        if (!clipboard) return false;
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_paste_special_${mode}_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetPasteSpecialPayload>({
            command: 'sheet.pasteSpecial',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                start_row: Math.max(0, Math.floor(Number(startRow) || 0)),
                start_col: Math.max(0, Math.floor(Number(startCol) || 0)),
                clipboard,
                mode,
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        const label = mode === 'values'
            ? 'Paste values'
            : mode === 'transpose'
                ? 'Paste transpose'
                : mode === 'formats'
                    ? 'Paste formats'
                    : 'Paste formulas';
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label,
                operationIds: [opId],
            },
        });
        return true;
    }

    private async moveDataThroughEngine(srcCol: number, srcRow: number, width: number, height: number, destCol: number, destRow: number, opts?: { validationOverride?: boolean }): Promise<void> {
        const w = Math.max(0, Math.floor(Number(width) || 0));
        const h = Math.max(0, Math.floor(Number(height) || 0));
        if (w <= 0 || h <= 0) return;

        const startCol = Math.max(0, Math.floor(Number(srcCol) || 0));
        const startRow = Math.max(0, Math.floor(Number(srcRow) || 0));
        const targetCol = Math.max(0, Math.floor(Number(destCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(destRow) || 0));
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const clearOpId = `viewport_streamer_move_clear_${now}_${requestId}`;
        const setOpId = `viewport_streamer_move_set_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetMoveRangePayload>({
            command: 'sheet.moveRange',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                source_range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: startRow + h - 1,
                    end_col: startCol + w - 1,
                },
                target_start_row: targetRow,
                target_start_col: targetCol,
                clear_op_id: clearOpId,
                set_op_id: setOpId,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            targetCol,
            targetRow,
            () => { void this.moveDataThroughEngine(startCol, startRow, w, h, targetCol, targetRow, { validationOverride: true }); },
            opts?.validationOverride !== true,
            {
                operationLabel: 'Move cells',
                targetRange: buildSpreadsheetMutationTargetRange(sheetId, targetCol, targetRow, w, h),
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Move cells',
                operationIds: [clearOpId, setOpId],
            },
        });
        this.cellCache.clear();
    }

    copyRangeFormats(srcCol: number, srcRow: number, width: number, height: number, destCol: number, destRow: number, targetWidth?: number, targetHeight?: number): void {
        const sourceWidth = Math.max(0, Math.floor(Number(width) || 0));
        const sourceHeight = Math.max(0, Math.floor(Number(height) || 0));
        if (sourceWidth <= 0 || sourceHeight <= 0) return;

        const startCol = Math.max(0, Math.floor(Number(srcCol) || 0));
        const startRow = Math.max(0, Math.floor(Number(srcRow) || 0));
        const targetCol = Math.max(0, Math.floor(Number(destCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(destRow) || 0));
        const paintWidth = Math.max(1, Math.floor(Number(targetWidth) || sourceWidth));
        const paintHeight = Math.max(1, Math.floor(Number(targetHeight) || sourceHeight));
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_copy_formats_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetCopyRangeFormatsToPayload>({
            command: 'sheet.copyRangeFormatsTo',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                source_range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: startRow + sourceHeight - 1,
                    end_col: startCol + sourceWidth - 1,
                },
                target_range: {
                    sheet_id: sheetId,
                    start_row: targetRow,
                    start_col: targetCol,
                    end_row: targetRow + paintHeight - 1,
                    end_col: targetCol + paintWidth - 1,
                },
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Format painter',
                operationIds: [opId],
            },
        });
        this.cellCache.clear();
    }

    private async copyDataThroughEngine(srcCol: number, srcRow: number, width: number, height: number, destCol: number, destRow: number, opts?: { validationOverride?: boolean }): Promise<void> {
        const w = Math.max(0, Math.floor(Number(width) || 0));
        const h = Math.max(0, Math.floor(Number(height) || 0));
        if (w <= 0 || h <= 0) return;

        const startCol = Math.max(0, Math.floor(Number(srcCol) || 0));
        const startRow = Math.max(0, Math.floor(Number(srcRow) || 0));
        const targetCol = Math.max(0, Math.floor(Number(destCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(destRow) || 0));
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const clearOpId = `viewport_streamer_copy_clear_${now}_${requestId}`;
        const setOpId = `viewport_streamer_copy_set_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetCopyRangeToPayload>({
            command: 'sheet.copyRangeTo',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                source_range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: startRow + h - 1,
                    end_col: startCol + w - 1,
                },
                target_start_row: targetRow,
                target_start_col: targetCol,
                clear_op_id: clearOpId,
                set_op_id: setOpId,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            targetCol,
            targetRow,
            () => { void this.copyDataThroughEngine(startCol, startRow, w, h, targetCol, targetRow, { validationOverride: true }); },
            opts?.validationOverride !== true,
            {
                operationLabel: 'Copy cells',
                targetRange: buildSpreadsheetMutationTargetRange(sheetId, targetCol, targetRow, w, h),
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Copy cells',
                operationIds: [clearOpId, setOpId],
            },
        });
        this.cellCache.clear();
    }

    clearRange(
        startCol: number,
        startRow: number,
        width: number,
        height: number,
        opts?: { sheetId?: number | null; skipUndoRegistration?: boolean },
    ) {
        const w = Math.max(0, Math.floor(Number(width) || 0));
        const h = Math.max(0, Math.floor(Number(height) || 0));
        if (w <= 0 || h <= 0) return;

        const col = Math.max(0, Math.floor(Number(startCol) || 0));
        const row = Math.max(0, Math.floor(Number(startRow) || 0));
        const uiSheetId = Number.isFinite(Number(opts?.sheetId)) && Number(opts?.sheetId) > 0
            ? Math.floor(Number(opts?.sheetId))
            : (this.pinnedSheetId ?? this.activeSheetId);
        const sheetId = buildSpreadsheetEngineSheetId(uiSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_clear_range_${Date.now()}_${requestId}`,
            op_type: 'ClearRange',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: row,
                    start_col: col,
                    end_row: row + h - 1,
                    end_col: col + w - 1,
                },
            },
        });
        const sent = this.sendSpreadsheetEngineOperations([operation], uiSheetId, {
            label: 'Clear cells',
            skipUndoRegistration: opts?.skipUndoRegistration,
        });
        if (sent) {
            this.cellCache.clear();
            this.notifyProjectionChanged();
        }
    }

    private async fillDataThroughEngine(
        srcCol: number,
        srcRow: number,
        srcW: number,
        srcH: number,
        destCol: number,
        destRow: number,
        destW: number,
        destH: number,
        mode: SpreadsheetFillRangeMode,
        opts?: { validationOverride?: boolean },
    ): Promise<void> {
        const sourceW = Math.max(0, Math.floor(Number(srcW) || 0));
        const sourceH = Math.max(0, Math.floor(Number(srcH) || 0));
        const targetW = Math.max(0, Math.floor(Number(destW) || 0));
        const targetH = Math.max(0, Math.floor(Number(destH) || 0));
        if (sourceW <= 0 || sourceH <= 0 || targetW <= 0 || targetH <= 0) return;

        const startCol = Math.max(0, Math.floor(Number(srcCol) || 0));
        const startRow = Math.max(0, Math.floor(Number(srcRow) || 0));
        const targetCol = Math.max(0, Math.floor(Number(destCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(destRow) || 0));
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_fill_${now}_${requestId}`;
        const command = buildSpreadsheetWorkerCommand<SpreadsheetFillRangePayload>({
            command: 'sheet.fillRange',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                source_range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: startRow + sourceH - 1,
                    end_col: startCol + sourceW - 1,
                },
                target_range: {
                    sheet_id: sheetId,
                    start_row: targetRow,
                    start_col: targetCol,
                    end_row: targetRow + targetH - 1,
                    end_col: targetCol + targetW - 1,
                },
                mode,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            targetCol,
            targetRow,
            () => { void this.fillDataThroughEngine(startCol, startRow, sourceW, sourceH, targetCol, targetRow, targetW, targetH, mode, { validationOverride: true }); },
            opts?.validationOverride !== true,
            {
                operationLabel: 'Fill cells',
                targetRange: buildSpreadsheetMutationTargetRange(sheetId, targetCol, targetRow, targetW, targetH),
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Fill cells',
                operationIds: [opId],
            },
        });
        this.cellCache.clear();
    }

    private sendBoundCrosstabProjectionSetRangeInput(
        startCol: number,
        startRow: number,
        data: string[][],
        uiSheetId?: number | null
    ): boolean {
        const rows = Array.isArray(data) ? data.map((row) => (
            Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []
        )) : [];
        const rowCount = rows.length;
        const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
        if (rowCount <= 0 || colCount <= 0) return false;

        const normalizedRows = rows.map((row) => {
            if (row.length === colCount) return row;
            return [...row, ...new Array(colCount - row.length).fill('')];
        });
        const targetUiSheetId = Number.isFinite(Number(uiSheetId)) && Number(uiSheetId) > 0
            ? Math.floor(Number(uiSheetId))
            : (this.pinnedSheetId ?? this.activeSheetId);
        const sheetId = buildSpreadsheetEngineSheetId(targetUiSheetId);
        const requestId = Math.random().toString(36).substring(7);
        const operation = buildSpreadsheetOperationEnvelope({
            op_id: `viewport_streamer_set_range_${Date.now()}_${requestId}`,
            op_type: 'SetRangeInput',
            workbook_id: 'active_workbook',
            sheet_id: sheetId,
            base_revision: 0,
            actor_client_id: 'viewport_streamer',
            created_at_ms: Date.now(),
            payload: {
                range: {
                    sheet_id: sheetId,
                    start_row: startRow,
                    start_col: startCol,
                    end_row: startRow + rowCount - 1,
                    end_col: startCol + colCount - 1,
                },
                raw_inputs: normalizedRows,
            },
        });
        const command = buildSpreadsheetWorkerCommand<SpreadsheetApplyOpsPayload>({
            command: 'sheet.applyOps',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                ops: [operation],
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.sendCommandWithPendingUndo(command, { publishAcceptedOperations: false });
        return true;
    }

    setBoundCrosstabProjectionData(
        startCol: number,
        startRow: number,
        data: string[][],
        opts?: {
            sheetId?: number | null;
        }
    ) {
        this.sendBoundCrosstabProjectionSetRangeInput(startCol, startRow, data, opts?.sheetId);
        if (this.lastViewport) {
            const vp = this.lastViewport;
            this.lastViewport = null;
            this.updateViewport(vp.x, vp.y, vp.width, vp.height);
        }

        // Invalidate cache for range?
        // Or just wait for next viewport update.
        // We should probably request a viewport update immediately.
        // But we don't have x/y/w/h here easily.
        // The GridController will call updateViewport on scroll/render.
    }

    pasteData(startCol: number, startRow: number, text: string, opts?: { validationOverride?: boolean }) {
        const sheetId = this.engineSheetIdForUiSheet();
        const requestId = Math.random().toString(36).substring(7);
        const now = Date.now();
        const opId = `viewport_streamer_paste_${now}_${requestId}`;
        const targetRow = Math.max(0, Math.floor(Number(startRow) || 0));
        const targetCol = Math.max(0, Math.floor(Number(startCol) || 0));
        const rawTsv = String(text ?? '');
        const tsv = this.options.transformPastedText
            ? this.options.transformPastedText({ sheetId, startCol: targetCol, startRow: targetRow, text: rawTsv })
            : rawTsv;
        const pasteShape = getTsvShape(tsv);
        const command = buildSpreadsheetWorkerCommand<SpreadsheetPasteTsvPayload>({
            command: 'sheet.pasteTsv',
            requestId,
            workbookId: 'active_workbook',
            sheetId,
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                sheet_id: sheetId,
                start_row: targetRow,
                start_col: targetCol,
                tsv,
                ...(opts?.validationOverride ? { validation_override: true } : {}),
                op_id: opId,
                actor_client_id: 'viewport_streamer',
                created_at_ms: now,
                allowQueryTabEdit: this.options.allowQueryTabEdits,
            },
        });
        this.trackPendingValidationOverrideMutation(
            requestId,
            targetCol,
            targetRow,
            () => this.pasteData(targetCol, targetRow, tsv, { validationOverride: true }),
            opts?.validationOverride !== true,
            {
                operationLabel: 'Paste cells',
                targetRange: buildSpreadsheetMutationTargetRange(sheetId, targetCol, targetRow, pasteShape.width, pasteShape.height),
            },
        );
        this.sendCommandWithPendingUndo(command, {
            publishAcceptedOperations: true,
            undo: {
                label: 'Paste cells',
                operationIds: [opId],
            },
        });
    }

    private sendCommandWithPendingUndo<TPayload>(
        command: SpreadsheetEngineWorkerCommand<TPayload>,
        options?: PendingMutationCommandOptions,
    ) {
        if (options?.publishAcceptedOperations) {
            this.pendingAcceptedOperationPublicationRequestIds.add(command.requestId);
        }
        const undo = options?.undo;
        if (undo) {
            this.trackPendingUndoableMutation(
                command.requestId,
                undo.label,
                undo.operationIds,
                undo.skipUndoRegistration,
            );
        }
        this.bus.sendCommand(command.command, command);
    }

    private trackPendingUndoableMutation(
        requestId: string,
        label: string,
        operationIds: string[],
        skipUndoRegistration?: boolean,
    ) {
        if (skipUndoRegistration) return;
        const ids = operationIds.map((id) => String(id ?? '').trim()).filter(Boolean);
        if (!requestId || ids.length <= 0) return;
        const pending: PendingUndoableMutation = {
            requestId,
            label,
            operationIds: new Set(ids),
        };
        this.pendingUndoableMutationsByRequestId.set(requestId, pending);
        for (const id of pending.operationIds) {
            this.pendingUndoableMutationsByOperationId.set(id, pending);
        }
    }

    private clearPendingUndoableMutation(pending: PendingUndoableMutation) {
        this.pendingUndoableMutationsByRequestId.delete(pending.requestId);
        for (const id of pending.operationIds) {
            this.pendingUndoableMutationsByOperationId.delete(id);
        }
    }

    private trackPendingValidationOverrideMutation(
        requestId: string,
        col: number,
        row: number,
        retryWithValidationOverride: () => void,
        enabled: boolean,
        context?: {
            targetRange?: SpreadsheetRangeAddress;
            operationLabel?: string;
        },
    ) {
        if (!enabled) return;
        this.pendingValidationOverrideMutations.set(requestId, {
            col: Math.max(0, Math.floor(Number(col) || 0)),
            row: Math.max(0, Math.floor(Number(row) || 0)),
            targetRange: context?.targetRange,
            operationLabel: context?.operationLabel,
            retryWithValidationOverride,
        });
    }

    private emitUndoableMutationsForAcceptedOperations(operations: SpreadsheetEngineOperationEnvelope[]) {
        const acceptedPending = new Set<PendingUndoableMutation>();
        for (const operation of operations) {
            const pending = this.pendingUndoableMutationsByOperationId.get(operation.op_id);
            if (pending) acceptedPending.add(pending);
        }
        for (const pending of acceptedPending) {
            this.clearPendingUndoableMutation(pending);
            this.emitUndoableMutation(pending.label);
        }
    }

    private emitUndoableMutation(label: string) {
        if (!this.onUndoableMutation) return;
        this.onUndoableMutation({
            label,
            undo: () => this.undo(),
            redo: () => this.redo(),
        });
    }

    updateSharedState(sab: SharedArrayBuffer) {
        const requestId = Math.random().toString(36).substring(7);
        const command = buildSpreadsheetWorkerCommand<SpreadsheetPresenceUpdatePayload>({
            command: 'presence.update',
            requestId,
            workbookId: 'active_workbook',
            sheetId: this.engineSheetIdForUiSheet(),
            baseRevision: 0,
            clientId: 'viewport_streamer',
            payload: {
                kind: 'layout_shared_state',
                sharedStateBuffer: sab,
            },
        });
        this.bus.sendCommand(command.command, command);
    }

}
